import React, { useState, useEffect, useCallback, useMemo } from 'react';
import LoadError from '../../components/admin/LoadError';
import FilterListIcon from '@mui/icons-material/FilterList';
import StatusChip from '../../components/admin/StatusChip';
import RowActions from '../../components/admin/RowActions';
import { clampSx, EMPTY, formatMoney, humanize, orDash } from '../../utils/adminFormat';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  Chip,
  MenuItem,
  ListSubheader,
  FormControl,
  InputLabel,
  Select,
  Skeleton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Checkbox,
  InputAdornment,
  Autocomplete,
  OutlinedInput,
  Alert,
  FormHelperText,
  FormControlLabel,
  Switch,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  AccessTime as TimeIcon,
  Quiz as QuizIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Subject as SectionIcon,
  DragIndicator as DragIndicatorIcon,
  Download as DownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Papa from 'papaparse';
import { toast } from 'react-toastify';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import api from '../../api';
import canChangeProductPrice, { stripePriceNote } from '../../utils/canChangeProductPrice';
import SimilarityReportDialog, {
  SimilarityChip,
  SIMILARITY_FILTER_OPTIONS,
  appendSimilarityParams,
} from '../QuestionBank/components/SimilarityReportDialog';
import { WebSimilarityChip } from '../QuestionBank/components/WebSimilaritySection';
import BulkWebCheckDialog from '../QuestionBank/components/BulkWebCheckDialog';
import { Language as LanguageIcon } from '@mui/icons-material';
import { adminHasPermission } from '../../utils/adminPermissions';
import { SCHOOL_TYPE_OPTIONS, groupSchoolsForMenu, schoolMatchesCategory, consortiumOptionsForMenu, examAreaOptionsForMenu, examAreaSelectValue, applyExamAreaToFilters, keptExamArea, appendSchoolAssessmentParams } from '../../utils/schoolCategoryFilter';
import HtmlWithLatex from '../QuestionBank/components/HtmlWithLatex';
import QuestionBankQuestionEditDialog from '../QuestionBank/components/QuestionBankQuestionEditDialog';
import ReactQuill from 'react-quill-new';
import { QUILL_MODULES, QUILL_FORMATS, plainTextLength } from '../../utils/quillQuestionEditorConfig';

function normalizeTargetSchoolIds(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((id) => (id === '' || id == null ? null : Number(id)))
    .filter((id) => id != null && !Number.isNaN(id));
}

/** Normalize options for the view dialog (array or legacy object shapes). */
function normalizeViewOptions(options) {
  if (!options) return [];
  if (Array.isArray(options)) {
    const arr = [...options];
    if (arr.some((o) => o?.order !== undefined)) {
      arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    return arr;
  }
  if (typeof options === 'object') {
    return Object.keys(options)
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => {
        const v = options[k];
        const text = typeof v === 'string' ? v : v?.option_text ?? v?.option ?? '';
        return {
          id: k,
          option_text: text,
          is_correct: v?.is_correct,
          order: v?.order,
        };
      });
  }
  return [];
}

/** Merge legacy exam questions and question-bank questions for the admin view modal. */
function buildViewDialogQuestions(exam) {
  if (!exam) return [];
  const legacy = (exam.questions || [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((q) => {
      const correct = q.correct_answer;
      return {
        key: `legacy-${q.id}`,
        source: 'legacy',
        id: q.id,
        question_text: q.question_text,
        marks: q.marks,
        duration_in_sec: q.duration_in_sec,
        type: null,
        category_name: null,
        options: normalizeViewOptions(q.options),
        answer: null,
        isCorrectOption: (opt) =>
          correct != null && String(opt.option_text) === String(correct),
      };
    });
  const bank = (exam.question_bank_questions || [])
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((q) => ({
      key: `bank-${q.id}`,
      source: 'bank',
      id: q.id,
      question_text: q.question_text,
      marks: q.marks,
      duration_in_sec: q.duration_sec,
      type: q.type,
      category_name: q.category_name,
      options: normalizeViewOptions(q.options),
      answer: q.answer ?? null,
      isCorrectOption: (opt) => !!opt.is_correct,
    }));
  return [...legacy, ...bank];
}

function appendSchoolTargetsToFormData(formData, targetSchoolIds) {
  formData.append('school_targets_sync', '1');
  const ids = normalizeTargetSchoolIds(targetSchoolIds);
  ids.forEach((id) => {
    formData.append('target_school_ids[]', id);
  });
  if (ids.length > 0) {
    formData.append('school_id', ids[0]);
  }
}

function appendSubjectIdsToFormData(formData, subjectIds) {
  const ids = Array.isArray(subjectIds) ? subjectIds : [];
  ids.forEach((id) => {
    if (id !== '' && id != null && !Number.isNaN(Number(id))) {
      formData.append('subject_ids[]', Number(id));
    }
  });
}

function findMockExamCategoryId(categories) {
  const stack = Array.isArray(categories) ? [...categories] : [];
  while (stack.length) {
    const cat = stack.shift();
    if (String(cat?.name || '').trim().toLowerCase() === 'mock exam') {
      return cat.id;
    }
    if (Array.isArray(cat?.all_children) && cat.all_children.length) {
      stack.push(...cat.all_children);
    }
    if (Array.isArray(cat?.children) && cat.children.length) {
      stack.push(...cat.children);
    }
  }
  return '';
}

/** Single price string — avoids mixing a fixed currency symbol with other symbols in the label */
function formatExamPrice(price, currency) {
  const sym = (currency || '£').trim();
  const n = Number(price);
  const s = Number.isFinite(n) ? n.toFixed(2) : String(price ?? '0');
  return `${s} ${sym}`;
}

// Validation schema
const examSchema = yup.object().shape({
  name: yup.string()
    .required('Mock exam name is required')
    .min(5, 'Name must be at least 5 characters')
    .max(255, 'Name must not exceed 255 characters'),
  description: yup.string()
    .nullable()
    .transform((value, originalValue) => {
    return originalValue === '' ? null : value;
  }),
  original_paper_link: yup.string()
    .nullable()
    .max(2048, 'Original paper link must not exceed 2048 characters')
    .transform((value, originalValue) => {
      return originalValue === '' ? null : value;
    }),
  category_id: yup.number()
    .required('Category is required')
    .integer('Category must be an integer')
    .transform((value, originalValue) => {
      // Convert empty string to undefined to trigger required validation
      return originalValue === '' ? undefined : value;
    })
    .typeError('Category must be selected'),
  format: yup.number()
    .required('Format is required')
    .integer('Format must be an integer')
    .transform((value, originalValue) => {
      // Convert empty string to undefined to trigger required validation
      return originalValue === '' ? undefined : value;
    })
    .typeError('Format must be selected'),
  subject_ids: yup
    .array()
    .of(yup.number().integer())
    .min(1, 'Select at least one subject')
    .required('Select at least one subject'),
  school_year_id: yup.number()
    .required('School year is required')
    .integer('School year must be an integer')
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .typeError('School year must be selected'),
  price: yup.number()
    .required('Price is required')
    .min(0, 'Price must be at least 0')
    .typeError('Price must be a number'),
  currency: yup
    .string()
    .nullable()
    .oneOf(['€', '$', '£', '', null], 'Currency must be one of: €, $, £'),
  duration_minutes: yup
    .mixed()
    .nullable()
    .test(
      'duration_ok',
      'Duration must be at least 1 minute (or leave empty to use 60)',
      (v) => v === '' || v == null || v === undefined || (!Number.isNaN(Number(v)) && Number(v) >= 1)
    ),
  target_school_ids: yup
    .array()
    .of(yup.number().integer('Each school id must be an integer'))
    .nullable()
    .default([]),
  max_attempts: yup.number()
    .nullable()
    .integer('Retake attempts must be an integer')
    .min(0, 'Retake attempts must be at least 0')
    .transform((value, originalValue) => originalValue === '' ? null : value)
    .typeError('Retake attempts must be a number'),
  marks_per_correct_answer: yup.number()
    .nullable()
    .min(0, 'Marks per correct answer must be at least 0')
    .transform((value, originalValue) => originalValue === '' ? null : value)
    .typeError('Marks per correct answer must be a number'),
  negative_marks_penalty: yup.number()
    .nullable()
    .min(0, 'Negative marks penalty must be at least 0')
    .transform((value, originalValue) => originalValue === '' ? null : value)
    .typeError('Negative marks penalty must be a number'),
  instructions: yup.string()
    .nullable()
    .transform((value, originalValue) => originalValue === '' ? null : value),
  held_date: yup
    .string()
    .nullable()
    .transform((value, originalValue) => originalValue === '' ? null : value),
  held_time: yup
    .string()
    .nullable()
    .transform((value, originalValue) => originalValue === '' ? null : value),
  location_ids: yup
    .array()
    .of(yup.number().integer('Each location id must be an integer'))
    .when('format', {
      is: (v) => String(v || '').toLowerCase().includes('physical'),
      then: (s) => s.min(1, 'Select at least one location').required('Location is required for physical exams'),
      otherwise: (s) => s.default([]),
    }),
  mock_exam_image: yup.mixed()
    .nullable()
    .test('fileSize', 'Mock exam image size must not exceed 10MB', (value) => {
    if (!value) return true; // Allow empty
    return value.size <= 10 * 1024 * 1024; // 10MB in bytes
    })
    .test('fileType', 'The uploaded file must be an image', (value) => {
    if (!value) return true; // Allow empty
    return value.type.startsWith('image/');
  }),
  questions: yup.array()
    .min(0)
    .transform((value) => {
      // Remove empty placeholder questions before validation
      if (!Array.isArray(value)) return value;
      return value.filter((q) => q && plainTextLength(q.question) >= 10);
    })
    .of(
    yup.object().shape({
        question: yup.string()
          .required('Each question is required')
          .test(
            'minPlain',
            'Each question must be at least 10 characters',
            (val) => plainTextLength(val) >= 10
          )
          .test(
            'maxPlain',
            'Each question must not exceed 500 characters of text',
            (val) => plainTextLength(val) <= 500
          ),
        options: yup.object()
          .test('minOptions', 'Each question must have at least two options', function(value) {
            if (!value) return false;
            const optionValues = Object.values(value).filter(opt => opt && opt.trim() !== '');
            return optionValues.length >= 2;
          })
          .test('optionLength', 'Each option must be between 1 and 255 characters', function(value) {
            if (!value) return false;
            for (const opt of Object.values(value)) {
              if (opt && opt.trim() !== '') {
                if (opt.length < 1 || opt.length > 255) {
                  return this.createError({ message: 'Each option must be between 1 and 255 characters' });
                }
              }
            }
            return true;
          })
          .test('nonEmptyOptions', 'Each option must be a non-empty string', function(value) {
            if (!value) return false;
            for (const [, opt] of Object.entries(value)) {
              if (opt && opt.trim() !== '') {
                // Option is provided and non-empty, which is valid
                continue;
              }
            }
            return true;
          })
          .shape({
            1: yup.string().nullable().transform((v) => v === '' ? null : v),
            2: yup.string().nullable().transform((v) => v === '' ? null : v),
            3: yup.string().nullable().transform((v) => v === '' ? null : v),
            4: yup.string().nullable().transform((v) => v === '' ? null : v)
      }),
        answer: yup.string()
          .required('Each question must have one answer')
          .min(1, 'Answer must be at least 1 character')
          .max(255, 'Answer must not exceed 255 characters')
          .test('answerInOptions', 'The answer must match one of the options', function(value) {
            const { options } = this.parent;
            if (!options || !value) return true; // Let required validation handle empty
            const optionValues = Object.values(options)
              .filter(opt => opt && opt.trim() !== '')
              .map(opt => opt.trim());
            return optionValues.includes(value.trim());
          }),
        duration_in_sec: yup.number()
          .required('Each question must have a duration')
          .integer('Duration must be a number')
          .min(1, 'Duration must be at least 1 second')
          .typeError('Duration must be a number'),
        marks: yup.number()
          .nullable()
          .integer('Marks must be a number')
          .min(1, 'Marks must be at least 1')
          .transform((value, originalValue) => {
            return originalValue === '' ? null : value;
          })
          .typeError('Marks must be a number')
      })
    )
    .test('arrayCountsMatch', 'The number of questions, options, answers, and durations must match', function(questions) {
      if (!questions || questions.length === 0) return true;
      
      const questionCount = questions.length;
      const counts = {
        questions: questionCount,
        options: questions.filter(q => q.options && Object.values(q.options).some(opt => opt && opt.trim() !== '')).length,
        answers: questions.filter(q => q.answer && q.answer.trim() !== '').length,
        durations: questions.filter(q => q.duration_in_sec != null).length,
        marks: questions.filter(q => q.marks != null).length
      };
      
      // Check if all arrays have same count (required arrays)
      const requiredMatch = counts.options === questionCount && 
                           counts.answers === questionCount && 
                           counts.durations === questionCount;
      
      // If marks are provided, they must also match the count
      const marksMatch = counts.marks === 0 || counts.marks === questionCount;
      
      return requiredMatch && marksMatch;
    })
});

const gradientButtonStyle = {
  background: 'linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)',
  color: '#fff',
  fontWeight: 600,
  paddingX: 2,
  paddingY: 1,
  borderRadius: 2,
  textTransform: 'none',
  '&:hover': {
    background: 'linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)',
    opacity: 0.9,
  }
};

// Helper function to format only question-related validation errors into user-friendly messages
// Regular field errors are displayed under their respective input fields
const formatQuestionErrors = (errors) => {
  const errorMessages = [];
  const fieldLabels = {
    question: 'Question',
    answer: 'Correct Answer',
    duration_in_sec: 'Duration (seconds)',
    marks: 'Marks'
  };

  const processErrors = (errObj, prefix = '') => {
    if (!errObj || typeof errObj !== 'object') return;

    Object.keys(errObj).forEach(key => {
      const error = errObj[key];
      
      // If error has a message property, it's a direct error
      if (error?.message) {
        const fieldLabel = fieldLabels[key] || key;
        errorMessages.push(`${prefix}${fieldLabel}: ${error.message}`);
      } 
      // Handle options object (options.1, options.2, etc.)
      else if (key === 'options' && error && typeof error === 'object' && !Array.isArray(error)) {
        Object.keys(error).forEach(optKey => {
          const optError = error[optKey];
          const optionLabel = `Option ${optKey}`;
          if (optError?.message) {
            errorMessages.push(`${prefix}${optionLabel}: ${optError.message}`);
          } else if (optError && typeof optError === 'object') {
            // Handle nested option errors
            processErrors(optError, `${prefix}${optionLabel} - `);
          }
        });
      }
      // Handle array errors (questions array)
      else if (Array.isArray(error)) {
        error.forEach((item, index) => {
          if (item && typeof item === 'object') {
            processErrors(item, `Question ${index + 1} - `);
          } else if (item?.message) {
            // Handle direct array error messages
            errorMessages.push(`${prefix}Question ${index + 1}: ${item.message}`);
          }
        });
      } 
      // Handle nested object errors
      else if (error && typeof error === 'object') {
        processErrors(error, prefix);
      }
    });
  };

  // Only process question-related errors
  if (errors?.questions) {
    processErrors({ questions: errors.questions });
  }

  return errorMessages;
};

const SortableMockQuestionRow = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
};

const MockExam = () => {
  const location = useLocation();
  const [exams, setExams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formats, setFormats] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [schools, setSchools] = useState([]);
  const [filterSchools, setFilterSchools] = useState([]);
  const [regions, setRegions] = useState([]);
  const [schoolAssessments, setSchoolAssessments] = useState({ open: false, school: null, papers: [], mock_exams: [] });
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  // Search + Category, Subject, Status, Target school stay visible; the rest sit behind "More filters".
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    category_id: '',
    school_category: '',
    school_id: '',
    consortium_id: '',
    exam_school_id: '',
    region_id: '',
    nation: '',
    subject_id: '',
    similarity: '',
    status: '',
  });
  const hiddenFilterCount = ['school_category', 'nation', 'region_id', 'consortium_id', 'exam_school_id', 'similarity']
    .filter((k) => filters[k] !== '' && filters[k] != null).length;
  // Paper-level plagiarism report for a mock exam row
  const [similarityTarget, setSimilarityTarget] = useState(null);
  const [bulkWebOpen, setBulkWebOpen] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [viewingExam, setViewingExam] = useState(null);
  const viewDialogQuestions = useMemo(
    () => buildViewDialogQuestions(viewingExam),
    [viewingExam]
  );
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    exam: null,
    deleteQuestionBankToo: false,
  });
  const [deletingExamId, setDeletingExamId] = useState(null);
  const [togglingExamId, setTogglingExamId] = useState(null);
  const [downloadingAnswerKeyExamId, setDownloadingAnswerKeyExamId] = useState(null);
  const [viewingPdfExamId, setViewingPdfExamId] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalExams, setTotalExams] = useState(0);

  const canViewMockExams = adminHasPermission('view-mock-exams');
  const canCreateMockExams = adminHasPermission('create-mock-exams');
  const canUpdateMockExams = adminHasPermission('update-mock-exams');
  const canDeleteMockExams = adminHasPermission('delete-mock-exams');
  const canDeleteQuestionBank = adminHasPermission('delete-question-bank');
  const showFormTab = canCreateMockExams || canUpdateMockExams;
  const hasCreateTab = canCreateMockExams;

  // --- Question Bank Integration ---
  const [selectedBankQuestions, setSelectedBankQuestions] = useState([]);
  const [bankPickerOpen, setBankPickerOpen] = useState(false);
  const [bankPickerQuestions, setBankPickerQuestions] = useState([]);
  const [bankPickerLoading, setBankPickerLoading] = useState(false);
  const [bankPickerPage, setBankPickerPage] = useState(0);
  const [bankPickerTotal, setBankPickerTotal] = useState(0);
  const [bankPickerSearch, setBankPickerSearch] = useState('');
  const [bankPickerType, setBankPickerType] = useState('');
  const [bankPickerSubjectId, setBankPickerSubjectId] = useState('');
  const [bankPickerTopicIds, setBankPickerTopicIds] = useState([]);
  const [bankPickerTopicCatalog, setBankPickerTopicCatalog] = useState([]);
  const [bankPickerSelected, setBankPickerSelected] = useState([]);

  /** CSV bulk import feedback (filename + parsing + result) */
  const [csvImportState, setCsvImportState] = useState({
    loading: false,
    fileName: '',
    message: '',
    severity: 'info',
  });

  useEffect(() => {
    if (selectedBankQuestions.length > 0) {
      setCsvImportState({ loading: false, fileName: '', message: '', severity: 'info' });
    }
  }, [selectedBankQuestions.length]);

  // --- Bank Question Inline Edit ---
  const [editingBankQuestion, setEditingBankQuestion] = useState(null);
  const [bankQuestionSaving, setBankQuestionSaving] = useState(false);
  const [scalingMarks, setScalingMarks] = useState(false);

  const fetchBankPickerQuestions = React.useCallback(async () => {
    setBankPickerLoading(true);
    try {
      const params = { page: bankPickerPage + 1, per_page: 5 };
      if (bankPickerSearch) params.search = bankPickerSearch;
      if (bankPickerType) params.type = bankPickerType;
      if (bankPickerSubjectId) params.subject_id = bankPickerSubjectId;
      if (Array.isArray(bankPickerTopicIds) && bankPickerTopicIds.length > 0) {
        params.topic_ids = bankPickerTopicIds;
      }
      const res = await api.get('/admin/question-bank', { params });
      const data = res.data?.data;
      setBankPickerQuestions(data?.data || []);
      setBankPickerTotal(data?.total || 0);
    } catch (err) {
      toast.error('Failed to load question bank');
    } finally {
      setBankPickerLoading(false);
    }
  }, [bankPickerPage, bankPickerSearch, bankPickerType, bankPickerSubjectId, bankPickerTopicIds]);

  React.useEffect(() => {
    if (!bankPickerSubjectId) {
      setBankPickerTopicCatalog([]);
      setBankPickerTopicIds([]);
      return;
    }
    let cancelled = false;
    api.get('/admin/question-bank/topics-by-subject', { params: { subject_id: bankPickerSubjectId } })
      .then((res) => { if (!cancelled) setBankPickerTopicCatalog(res.data?.data || []); })
      .catch(() => { if (!cancelled) setBankPickerTopicCatalog([]); });
    return () => { cancelled = true; };
  }, [bankPickerSubjectId]);

  React.useEffect(() => {
    if (!bankPickerOpen) return;
    const timer = setTimeout(fetchBankPickerQuestions, 300);
    return () => clearTimeout(timer);
  }, [bankPickerOpen, fetchBankPickerQuestions]);

  const toggleBankPickerSelect = (question) => {
    setBankPickerSelected((prev) =>
      prev.some((q) => q.id === question.id)
        ? prev.filter((q) => q.id !== question.id)
        : [...prev, question]
    );
  };

  const confirmBankSelection = () => {
    setSelectedBankQuestions((prev) => {
      const existingIds = new Set(prev.map((q) => q.id));
      const newOnes = bankPickerSelected.filter((q) => !existingIds.has(q.id));
      return [...prev, ...newOnes];
    });
    setBankPickerOpen(false);
    setBankPickerSelected([]);
    setBankPickerSearch('');
    setBankPickerType('');
    setBankPickerPage(0);
  };

  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const [mockSectionEdit, setMockSectionEdit] = useState(null);
  const [mockSectionSaving, setMockSectionSaving] = useState(false);

  const moveBankQuestion = (idx, direction) => {
    setSelectedBankQuestions((prev) => {
      const next = [...prev];
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= next.length) return prev;
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      return next;
    });
  };

  const handleMockDragEnd = ({ active, over }) => {
    if (!active?.id || !over?.id || String(active.id) === String(over.id)) return;
    setSelectedBankQuestions((prev) => {
      const ids = prev.map((q) => String(q.id));
      const oldIndex = ids.indexOf(String(active.id));
      const newIndex = ids.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const saveMockSectionEdit = async () => {
    if (!mockSectionEdit) return;
    setMockSectionSaving(true);
    const title = mockSectionEdit.title.trim() || null;
    const instructions = mockSectionEdit.instructions.trim() || null;
    try {
      await api.put(`/admin/question-bank/${mockSectionEdit.qId}`, {
        section_title: title,
        section_instructions: instructions,
      });
      setSelectedBankQuestions((prev) =>
        prev.map((q) =>
          q.id === mockSectionEdit.qId
            ? { ...q, section_title: title, section_instructions: instructions }
            : q
        )
      );
      setMockSectionEdit(null);
      toast.success('Section updated');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update section');
    } finally {
      setMockSectionSaving(false);
    }
  };

  const removeBankQuestion = (qId) => {
    setSelectedBankQuestions((prev) => prev.filter((q) => q.id !== qId));
  };

  const handleScaleTo100 = async () => {
    const getQMarks = (q) => parseInt(q.default_marks, 10) || 1;
    const total = selectedBankQuestions.reduce((s, q) => s + getQMarks(q), 0);
    if (total === 100) { toast.info('Already at 100 marks'); return; }
    if (total === 0) { toast.error('No marks assigned yet'); return; }

    const rawMarks = selectedBankQuestions.map(q => getQMarks(q) * 100 / total);
    const floored = rawMarks.map(v => Math.max(1, Math.floor(v)));
    const deficit = 100 - floored.reduce((s, v) => s + v, 0);
    const order = rawMarks.map((v, i) => ({ i, frac: v - Math.floor(v) })).sort((a, b) => b.frac - a.frac);
    for (let k = 0; k < deficit; k++) floored[order[k].i]++;

    setScalingMarks(true);
    try {
      const next = [...selectedBankQuestions];
      for (let idx = 0; idx < next.length; idx++) {
        const q = next[idx];
        const newTotal = floored[idx];
        if (q.default_marks !== newTotal) {
          await api.put(`/admin/question-bank/${q.id}`, { default_marks: newTotal });
        }
        next[idx] = { ...q, default_marks: newTotal };
      }
      setSelectedBankQuestions(next);
      toast.success('Marks scaled to 100 total');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to scale marks');
    } finally {
      setScalingMarks(false);
    }
  };

  const openBankQuestionEdit = async (q) => {
    try {
      const res = await api.get(`/admin/question-bank/${q.id}`);
      setEditingBankQuestion(res.data?.data || q);
    } catch {
      setEditingBankQuestion(q);
    }
  };

  const saveBankQuestionEdit = async (payload) => {
    setBankQuestionSaving(true);
    try {
      const res = await api.put(`/admin/question-bank/${editingBankQuestion.id}`, payload);
      const updated = res.data?.data || { ...editingBankQuestion, ...payload };
      setSelectedBankQuestions((prev) =>
        prev.map((q) => (q.id === editingBankQuestion.id ? { ...q, ...updated } : q))
      );
      setEditingBankQuestion(null);
      toast.success('Question updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update question');
    } finally {
      setBankQuestionSaving(false);
    }
  };

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(examSchema),
    defaultValues: {
      name: '',
      description: null,
      original_paper_link: '',
      category_id: '',
      format: '',
      subject_ids: [],
      school_year_id: '',
      price: '',
      currency: '£',
      duration_minutes: 60,
      held_date: '',
      held_time: '',
      due_at: '',
      location_ids: [],
      target_school_ids: [],
      max_attempts: 2,
      marks_per_correct_answer: 1,
      negative_marks_penalty: 0,
      instructions: '',
      questions: [{
        question: '',
        options: { 1: '', 2: '', 3: '', 4: '' },
        answer: '',
        duration_in_sec: '',
        marks: null
      }]
    }
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'questions'
  });

  // Handle CSV upload
  const handleCSVUpload = (e) => {
    const inputEl = e.target;
    const file = inputEl?.files?.[0];
    if (!file) {
      return;
    }
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Only .csv files are allowed.');
      setCsvImportState({
        loading: false,
        fileName: file.name,
        message: 'Invalid file type — choose a .csv file.',
        severity: 'error',
      });
      inputEl.value = '';
      return;
    }

    setCsvImportState({
      loading: true,
      fileName: file.name,
      message: 'Reading file…',
      severity: 'info',
    });

    /** Match column regardless of case, surrounding spaces, or Excel BOM on first header. */
    const getCsvField = (row, ...aliases) => {
      const want = new Set(aliases.map((a) => String(a).trim().toLowerCase()));
      for (const [k, v] of Object.entries(row)) {
        const key = String(k).replace(/^\uFEFF/, '').trim().toLowerCase();
        if (want.has(key)) {
          return v;
        }
      }
      return undefined;
    };

    const parsePositiveInt = (raw, fallback) => {
      if (raw === undefined || raw === null || raw === '') {
        return fallback;
      }
      const n = parseInt(String(raw).replace(/,/g, '').trim(), 10);
      return Number.isNaN(n) ? fallback : n;
    };

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const errors = [];
        const parsed = results.data.map((row, index) => {
          const question =
            getCsvField(row, 'question', 'question_text', 'text') ?? '';
          const options = {
            1: getCsvField(row, 'option1', 'option_1', 'opt1', 'a') ?? '',
            2: getCsvField(row, 'option2', 'option_2', 'opt2', 'b') ?? '',
            3: getCsvField(row, 'option3', 'option_3', 'opt3', 'c') ?? '',
            4: getCsvField(row, 'option4', 'option_4', 'opt4', 'd') ?? '',
          };
          const answer = getCsvField(row, 'answer', 'correct', 'correct_answer') ?? row.answer;

          const durationRaw = getCsvField(
            row,
            'duration_in_sec',
            'duration',
            'duration_sec',
            'duration_seconds',
            'seconds',
            'time_sec'
          );
          const marksRaw = getCsvField(row, 'marks', 'mark', 'points', 'score');

          const durationNum = parsePositiveInt(durationRaw, 30);
          const marksNum = parsePositiveInt(marksRaw, 1);

          const optionValues = Object.values(options).filter((v) => v && String(v).trim() !== '');
          if (optionValues.length < 2) errors.push(`Row ${index + 2}: Minimum 2 options required.`);
          if (!answer || !optionValues.includes(answer)) {
            errors.push(`Row ${index + 2}: Answer must match one of the options.`);
          }
          if (durationNum < 1) {
            errors.push(`Row ${index + 2}: Duration must be at least 1 second (column: duration_in_sec or duration).`);
          }
          if (marksNum < 1) {
            errors.push(`Row ${index + 2}: Marks must be at least 1 (column: marks).`);
          }

          return {
            question,
            options,
            answer,
            duration_in_sec: durationNum,
            marks: marksNum,
          };
        });

        if (errors.length > 0) {
          toast.error(`CSV Validation Failed:\n${errors.join('\n')}`);
          setCsvImportState({
            loading: false,
            fileName: file.name,
            message: `Validation failed (${errors.length} issue${errors.length === 1 ? '' : 's'}). Check the toast for details.`,
            severity: 'error',
          });
          inputEl.value = '';
          return;
        }

        replace(parsed);
        toast.success(`${parsed.length} questions loaded from CSV`);
        setCsvImportState({
          loading: false,
          fileName: file.name,
          message: `Loaded ${parsed.length} question${parsed.length === 1 ? '' : 's'} from CSV.`,
          severity: 'success',
        });
        inputEl.value = '';
      },
      error: () => {
        toast.error('Failed to parse CSV');
        setCsvImportState((prev) => ({
          ...prev,
          loading: false,
          message: 'Could not read this CSV file.',
          severity: 'error',
        }));
        inputEl.value = '';
      },
    });
  };

  // Suggestions for the exam search box: the first matches from the same admin list endpoint.
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchSuggestLoading, setSearchSuggestLoading] = useState(false);
  useEffect(() => {
    const text = String(filters.search || '').trim();
    if (text.length < 2) {
      setSearchSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setSearchSuggestLoading(true);
      api
        .get('admin/mock-exam', { params: { search: text, per_page: 8, page: 1 } })
        .then((res) => {
          if (!cancelled) setSearchSuggestions(res.data?.data?.data || []);
        })
        .catch(() => {
          if (!cancelled) setSearchSuggestions([]);
        })
        .finally(() => {
          if (!cancelled) setSearchSuggestLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [filters.search]);

  // Fetch exams
  const fetchExams = useCallback(async (applyFilters = false, overridePage = null, overridePerPage = null, overrideFilters = null) => {
    setLoading(true);
    setLoadError(null);
    const effectivePage = overridePage !== null ? overridePage : page;
    const effectivePerPage = overridePerPage !== null ? overridePerPage : rowsPerPage;
    const activeFilters = overrideFilters || filters;
    try {
      const params = new URLSearchParams();

      if (applyFilters) {
        if (activeFilters.search) params.append('search', activeFilters.search.trim());
        if (activeFilters.category_id) params.append('category_id', activeFilters.category_id);
        appendSchoolAssessmentParams(params, activeFilters);
        if (activeFilters.subject_id) params.append('subject_id', activeFilters.subject_id);
        if (activeFilters.similarity) appendSimilarityParams(params, activeFilters.similarity);
        if (activeFilters.status) params.append('status', activeFilters.status);
      }

      params.append('page', effectivePage + 1);
      params.append('per_page', effectivePerPage);
      // APP-01: the server sorts by name across all pages (sorting here only reordered one page).
      params.append('sort', 'name');

      const url = 'admin/mock-exam?' + params.toString();
      const response = await api.get(url);
      const paginationMeta = response.data.data;
      const examsData = paginationMeta.data || [];

      setTotalExams(paginationMeta.total || 0);
      // Store raw exams; format_name mapping is handled by a dedicated useEffect
      setExams(examsData);
    } catch (error) {
      setLoadError(error || true);
      toast.error('Failed to fetch exams');
      console.error('Error fetching exams:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchExams(true, newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newPerPage);
    setPage(0);
    fetchExams(true, 0, newPerPage);
  };

  // Fetch categories with tree structure
  const fetchCategories = async () => {
    try {
      const response = await api.get('admin/mock-exam/category-tree');
      const data = response.data.data || [];
      setCategories(data);
      return data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to regular categories API
      try {
        const fallbackResponse = await api.get('admin/mock-exam/categories');
        const data = fallbackResponse.data.data || [];
        setCategories(data);
        return data;
      } catch (fallbackError) {
        console.error('Fallback categories also failed:', fallbackError);
        if (canViewMockExams) fetchExams();
        return [];
      }
    }
  };

  // Flatten category tree for dropdown display
  const flattenCategories = (categories, level = 0) => {
    let flattened = [];
    categories.forEach(category => {
      flattened.push({
        id: category.id,
        name: category.name,
        level: level,
        originalName: category.name
      });
      
      // Use 'all_children' instead of 'children' based on API response
      if (category.all_children && category.all_children.length > 0) {
        flattened = flattened.concat(flattenCategories(category.all_children, level + 1));
      }
    });
    return flattened;
  };

  // Fetch formats from common data
  const fetchFormats = async () => {
    try {
      const response = await api.get('common/data?param=Formats');
      setFormats(response.data.data || []);
    } catch (error) {
      console.error('Error fetching formats:', error);
    }
  };

  const fetchSchoolYears = async () => {
    try {
      const response = await api.get('common/data?param=SchoolYears');
      setSchoolYears(response.data.data || []);
    } catch (error) {
      console.error('Error fetching school years:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await api.get('common/data?param=Subjects');
      setSubjects(response.data.data || []);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  // Fetch schools
  const fetchSchools = async () => {
    try {
      const [allRes, linkedRes] = await Promise.all([
        api.get('common/data?param=Schools'),
        api.get('common/data', { params: { param: 'Schools', linked_to: 'all_mock_exams' } }),
      ]);
      const raw = allRes?.data?.data;
      const linked = linkedRes?.data?.data;
      setSchools(Array.isArray(raw) ? raw : []);
      setFilterSchools(Array.isArray(linked) && linked.length ? linked : (Array.isArray(raw) ? raw : []));
    } catch (error) {
      console.error('Error fetching schools:', error);
      setSchools([]);
      setFilterSchools([]);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await api.get('common/data?param=Regions');
      setRegions(response.data.data || []);
    } catch (error) {
      setRegions([]);
    }
  };

  const openSchoolAssessments = async (schoolId) => {
    if (!schoolId) return;
    try {
      const response = await api.get(`admin/schools/${schoolId}/assessments`);
      const data = response.data?.data || {};
      setSchoolAssessments({
        open: true,
        school: data.school || null,
        papers: data.papers || [],
        mock_exams: data.mock_exams || [],
      });
    } catch (error) {
      toast.error('Failed to load school papers and mock exams');
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await api.get('common/data?param=Locations');
      setLocations(response.data.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    }
  };

  // Create or update exam
  const onSubmit = async (data) => {
    // Prevent double submission
    if (isSubmitting) {
      console.log('Form submission already in progress, ignoring duplicate request');
      return;
    }

    if (editingExam) {
      if (!canUpdateMockExams) {
        toast.error('You do not have permission to update mock exams.');
        return;
      }
    } else if (!canCreateMockExams) {
      toast.error('You do not have permission to create mock exams.');
      return;
    }

    console.log('Form submitted with data:', data);

    // Filter out completely empty question placeholders (default empty row)
    const filledQuestions = (data.questions || []).filter(
      (q) => q.question && plainTextLength(q.question) >= 10
    );

    // Validate: need either filled manual questions OR bank questions
    if (!editingExam && selectedBankQuestions.length === 0 && filledQuestions.length === 0) {
      toast.error('Please add at least one question manually or select questions from the Question Bank.');
      return;
    }

    const currencyResolved =
      data.currency && ['€', '$', '£'].includes(data.currency) ? data.currency : '£';
    const dm = data.duration_minutes;
    const durationResolved =
      dm === '' || dm == null || dm === undefined || Number.isNaN(Number(dm))
        ? 60
        : Math.max(1, parseInt(String(dm), 10));

    // Use only filled questions for submission (skip empty placeholder rows)
    const submitData = {
      ...data,
      questions: filledQuestions,
      currency: currencyResolved,
      duration_minutes: durationResolved,
    };

    const selectedFormat = formats.find((f) => Number(f.id) === Number(data.format));
    const isPhysicalFormat = !!selectedFormat?.name && String(selectedFormat.name).toLowerCase().includes('physical');

    // If not physical, force clear locations (avoid stale values)
    if (!isPhysicalFormat) {
      submitData.location_ids = [];
    }

    setIsSubmitting(true);

    try {
      if (editingExam) {
        // For edit mode, UpdateMockExamRequest uses 'sometimes' validation
        // Only send fields that are present (following 'sometimes' pattern)
        const hasImage = data.mock_exam_image && data.mock_exam_image instanceof File;
        const hasBankQuestions = selectedBankQuestions.length > 0;
        const filledManualQuestions = (data.questions || []).filter(
          (q) => q.question && plainTextLength(q.question) >= 10
        );
        const hasManualQuestions = filledManualQuestions.length > 0;

        // Use FormData if image, bank questions, or filled manual questions are present
        if (hasImage || hasBankQuestions || hasManualQuestions) {
          const formData = new FormData();
          formData.append('_method', 'PUT'); // Method spoofing for Laravel/PHP

          // Only append fields that have values (following 'sometimes' pattern)
          if (data.name) {
            formData.append('name', data.name);
          }

          if (data.description && data.description.trim() !== '') {
            formData.append('description', data.description);
          }
          if (data.original_paper_link !== undefined) {
            formData.append('original_paper_link', (data.original_paper_link || '').trim());
          }

          if (data.category_id) {
            formData.append('category_id', Number(data.category_id));
          }

          if (data.format) {
            formData.append('format_id', Number(data.format));
          }

          appendSubjectIdsToFormData(formData, data.subject_ids);

          if (data.school_year_id !== '' && data.school_year_id != null) {
            formData.append('school_year_id', Number(data.school_year_id));
          }

          if (data.held_date != null && data.held_date !== '') {
            formData.append('held_date', data.held_date);
          }
          if (data.held_time != null && data.held_time !== '') {
            formData.append('held_time', data.held_time);
          }
          if (data.due_at) {
            formData.append('due_at', new Date(data.due_at).toISOString());
          }
          const selectedFormat = formats.find((f) => Number(f.id) === Number(data.format));
          const isPhysicalFormat = !!selectedFormat?.name && String(selectedFormat.name).toLowerCase().includes('physical');
          if (isPhysicalFormat) {
            (data.location_ids || []).forEach((id) => {
              if (id !== '' && id != null && !Number.isNaN(Number(id))) {
                formData.append('location_ids[]', Number(id));
              }
            });
          }

          if (data.price !== null && data.price !== undefined) {
            formData.append('price', data.price);
          }

          formData.append('currency', submitData.currency);
          formData.append('is_free_preview', submitData.is_free_preview ? '1' : '0');
          formData.append('duration_minutes', submitData.duration_minutes);

          appendSchoolTargetsToFormData(formData, data.target_school_ids);

          if (data.max_attempts != null && data.max_attempts !== '' && !isNaN(data.max_attempts)) {
            formData.append('max_attempts', Math.max(0, Number(data.max_attempts)));
          }
          if (data.marks_per_correct_answer != null && data.marks_per_correct_answer !== '' && !isNaN(data.marks_per_correct_answer)) {
            formData.append('marks_per_correct_answer', Math.max(0, Number(data.marks_per_correct_answer)));
          }
          if (data.negative_marks_penalty != null && data.negative_marks_penalty !== '' && !isNaN(data.negative_marks_penalty)) {
            formData.append('negative_marks_penalty', Math.max(0, Number(data.negative_marks_penalty)));
          }
          if (data.instructions !== undefined) {
            formData.append('instructions', (data.instructions != null && String(data.instructions).trim() !== '') ? data.instructions.trim() : '');
          }

          if (hasImage) {
            formData.append('mock_exam_image', data.mock_exam_image);
          }

          // Send bank question IDs if bank questions are selected (replaces existing links)
          if (hasBankQuestions) {
            selectedBankQuestions.forEach((q, i) => {
              formData.append(`question_bank_question_ids[${i}]`, q.id);
            });
          } else if (hasManualQuestions) {
            // Send only filled manual questions (skip empty placeholder rows)
            filledManualQuestions.forEach((question, index) => {
              formData.append(`questions[${index}]`, question.question);

              const optionValues = Object.values(question.options)
                .filter(opt => opt && opt.trim() !== '');
              optionValues.forEach((optionValue, optIndex) => {
                formData.append(`options[${index}][${optIndex}]`, optionValue);
              });

              formData.append(`answers[${index}]`, question.answer);
              formData.append(`duration_in_sec[${index}]`, question.duration_in_sec);

              if (question.marks != null) {
                formData.append(`marks[${index}]`, question.marks);
              }
            });
          }

          // Use POST with _method=PUT for multipart/form-data updates
          const response = await api.post(`admin/mock-exam/${editingExam.id}`, formData);
          console.log('Update response:', response);
          toast.success(response.data.message || 'Exam updated successfully');
        } else {
          // Use JSON when no image and no questions (cleaner for simple updates)
          const updateData = {};
          
          // Only include fields that have values (following 'sometimes' pattern)
          if (data.name) {
            updateData.name = data.name;
          }
          
          if (data.description !== null && data.description !== undefined) {
            updateData.description = data.description || null;
          }
          if (data.original_paper_link !== undefined) {
            const link = (data.original_paper_link || '').trim();
            updateData.original_paper_link = link !== '' ? link : null;
          }
          
          if (data.category_id) {
            updateData.category_id = Number(data.category_id);
          }
          
          if (data.format) {
            updateData.format_id = Number(data.format);
          }

          if (Array.isArray(data.subject_ids) && data.subject_ids.length > 0) {
            updateData.subject_ids = data.subject_ids.map((id) => Number(id));
          }

          if (data.school_year_id !== '' && data.school_year_id != null) {
            updateData.school_year_id = Number(data.school_year_id);
          }

          if (data.held_date !== undefined) {
            updateData.held_date = data.held_date || null;
          }
          if (data.held_time !== undefined) {
            updateData.held_time = data.held_time || null;
          }
          if (data.due_at !== undefined) {
            updateData.due_at = data.due_at ? new Date(data.due_at).toISOString() : null;
          }
          if (data.location_ids !== undefined) {
            updateData.location_ids = Array.isArray(data.location_ids) ? data.location_ids.map((id) => Number(id)) : [];
          }
          
          if (data.price !== null && data.price !== undefined) {
            updateData.price = data.price;
          }
          
          if (data.currency && data.currency !== '') {
            updateData.currency = data.currency;
          }
          
          if (data.duration_minutes != null) {
            updateData.duration_minutes = data.duration_minutes;
          }
          
          const schoolIds = normalizeTargetSchoolIds(data.target_school_ids);
          updateData.school_targets_sync = true;
          updateData.target_school_ids = schoolIds;
          updateData.school_id = schoolIds.length > 0 ? schoolIds[0] : null;

          if (data.max_attempts != null && data.max_attempts !== '' && !isNaN(data.max_attempts)) {
            updateData.max_attempts = Math.max(0, Number(data.max_attempts));
          }
          if (data.marks_per_correct_answer != null && data.marks_per_correct_answer !== '' && !isNaN(data.marks_per_correct_answer)) {
            updateData.marks_per_correct_answer = Math.max(0, Number(data.marks_per_correct_answer));
          }
          if (data.negative_marks_penalty != null && data.negative_marks_penalty !== '' && !isNaN(data.negative_marks_penalty)) {
            updateData.negative_marks_penalty = Math.max(0, Number(data.negative_marks_penalty));
          }
          if (data.instructions !== undefined) {
            updateData.instructions = data.instructions && String(data.instructions).trim() !== '' ? data.instructions.trim() : null;
          }

          // Include bank question IDs if bank questions are selected (replaces existing links)
          if (selectedBankQuestions.length > 0) {
            updateData.question_bank_question_ids = selectedBankQuestions.map(q => q.id);
          }

          console.log('Update JSON payload:', updateData);

          const response = await api.put(`admin/mock-exam/${editingExam.id}`, updateData, {
            headers: {
              'Content-Type': 'application/json'
            }
          });
        console.log('Update response:', response);
          toast.success(response.data.message || 'Exam updated successfully');
        }
      } else {
        // For create mode, always use FormData (uses submitData which has filtered questions)
        const formData = new FormData();
        formData.append('name', submitData.name);

        // Guard nullable/optional fields — only send if they have real values
        if (submitData.description && submitData.description.trim() !== '') {
          formData.append('description', submitData.description);
        }
        if (submitData.original_paper_link !== undefined) {
          formData.append('original_paper_link', (submitData.original_paper_link || '').trim());
        }
        formData.append('category_id', Number(submitData.category_id));
        formData.append('format_id', Number(submitData.format));
        appendSubjectIdsToFormData(formData, submitData.subject_ids);
        formData.append('school_year_id', Number(submitData.school_year_id));
        if (submitData.held_date) {
          formData.append('held_date', submitData.held_date);
        }
        if (submitData.held_time) {
          formData.append('held_time', submitData.held_time);
        }
        if (submitData.due_at) {
          formData.append('due_at', new Date(submitData.due_at).toISOString());
        }
        const selectedFormat = formats.find((f) => Number(f.id) === Number(submitData.format));
        const isPhysicalFormat = !!selectedFormat?.name && String(selectedFormat.name).toLowerCase().includes('physical');
        if (isPhysicalFormat) {
          (submitData.location_ids || []).forEach((id) => {
            if (id !== '' && id != null && !Number.isNaN(Number(id))) {
              formData.append('location_ids[]', Number(id));
            }
          });
        }
        formData.append('price', submitData.price != null ? submitData.price : 0);

        formData.append('currency', submitData.currency);
        formData.append('is_free_preview', submitData.is_free_preview ? '1' : '0');
        formData.append('duration_minutes', submitData.duration_minutes);

        appendSchoolTargetsToFormData(formData, submitData.target_school_ids);

        if (submitData.mock_exam_image) {
          formData.append('mock_exam_image', submitData.mock_exam_image);
        }

        if (submitData.max_attempts != null && submitData.max_attempts !== '' && !isNaN(submitData.max_attempts)) {
          formData.append('max_attempts', Math.max(0, Number(submitData.max_attempts)));
        }
        if (submitData.marks_per_correct_answer != null && submitData.marks_per_correct_answer !== '' && !isNaN(submitData.marks_per_correct_answer)) {
          formData.append('marks_per_correct_answer', Math.max(0, Number(submitData.marks_per_correct_answer)));
        }
        if (submitData.negative_marks_penalty != null && submitData.negative_marks_penalty !== '' && !isNaN(submitData.negative_marks_penalty)) {
          formData.append('negative_marks_penalty', Math.max(0, Number(submitData.negative_marks_penalty)));
        }
        if (submitData.instructions && String(submitData.instructions).trim() !== '') {
          formData.append('instructions', submitData.instructions.trim());
        }

        // If bank questions are selected, include them inline (atomic — no separate link call)
        if (selectedBankQuestions.length > 0) {
          selectedBankQuestions.forEach((q, i) => {
            formData.append(`question_bank_question_ids[${i}]`, q.id);
          });
        } else {
          // Manual questions data — submitData.questions already filtered to filled questions only
          submitData.questions.forEach((question, index) => {
            formData.append(`questions[${index}]`, question.question);

            // Convert options object to array format (backend expects array)
            // Filter out empty options and convert to array with 0-based indexing
            const optionValues = Object.values(question.options)
              .filter(opt => opt && opt.trim() !== '');

            optionValues.forEach((optionValue, optIndex) => {
              formData.append(`options[${index}][${optIndex}]`, optionValue);
            });

            formData.append(`answers[${index}]`, question.answer);
            formData.append(`duration_in_sec[${index}]`, question.duration_in_sec);

            // Only append marks if provided (nullable in backend)
            if (question.marks != null) {
              formData.append(`marks[${index}]`, question.marks);
            }
          });
        }

        const response = await api.post('admin/mock-exam', formData);
        console.log('Create response:', response);
        toast.success(response.data.message || 'Exam created successfully');
      }

      setEditingExam(null);
      setSelectedBankQuestions([]);
      reset();
      if (canViewMockExams) {
        setTabValue(0);
        setPage(0);
        fetchExams(false, 0);
      } else {
        setTabValue(1);
      }
    } catch (error) {
      console.error('API Error:', error);
      console.error('Error response:', error.response);
      
      // Handle validation errors
      if (error.response?.status === 400 || error.response?.status === 422) {
        const errors = error.response.data.errors;
        if (errors) {
          Object.keys(errors).forEach(key => {
            const errorMessages = Array.isArray(errors[key]) ? errors[key] : [errors[key]];
            errorMessages.forEach(msg => toast.error(`${key}: ${msg}`));
          });
        } else {
          toast.error(error.response.data.message || 'Validation error occurred');
        }
      } else if (error.response?.status === 404) {
        toast.error('Mock exam not found');
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
      toast.error(error.response?.data?.message || 'Failed to save exam');
    }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to check if exam is active
  const isExamActive = (exam) => {
    if (!exam) return false;
    const statusValue = exam.status || exam.status_id || exam.exam_status || exam.state;
    if (statusValue === undefined || statusValue === null) {
      return false;
    }
    if (typeof statusValue === 'string') {
      const lowerStatus = statusValue.toLowerCase();
      if (lowerStatus === 'active' || lowerStatus === '2') {
        return true;
      }
      return false;
    }
    const statusNum = typeof statusValue === 'string' ? parseInt(statusValue, 10) : statusValue;
    // Status 2 = Active, Status 3 = Inactive
    return statusNum === 2;
  };

  // Delete exam
  const handleDeleteClick = (exam) => {
    if (!canDeleteMockExams) {
      toast.error('You do not have permission to delete mock exams.');
      return;
    }
    setDeleteDialog({ open: true, exam, deleteQuestionBankToo: false });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, exam: null, deleteQuestionBankToo: false });
  };

  const handleDeleteConfirm = async () => {
    const exam = deleteDialog.exam;
    if (!exam || !exam.id) {
      toast.error('Cannot delete: Exam ID not found');
      setDeleteDialog({ open: false, exam: null, deleteQuestionBankToo: false });
      return;
    }

    if (deleteDialog.deleteQuestionBankToo) {
      const proceed = window.confirm(
        'This will also delete related Question Bank questions that are only linked to this mock exam. Continue?'
      );
      if (!proceed) {
        return;
      }
    }

    setDeletingExamId(exam.id);
    
    try {
      const response = await api.delete(`admin/mock-exam/${exam.id}`, {
        data: {
          delete_linked_question_bank_questions: deleteDialog.deleteQuestionBankToo,
        },
      });
      if (response.data.success) {
        const deletedCount = response.data?.data?.deleted_question_ids?.length || 0;
        const skippedCount = response.data?.data?.skipped_question_ids?.length || 0;
        if (deleteDialog.deleteQuestionBankToo) {
          toast.success(`Exam deleted. Question Bank cleanup: ${deletedCount} deleted, ${skippedCount} skipped.`);
        } else {
          toast.success(response.data.message || 'Exam deleted successfully');
        }
        setDeleteDialog({ open: false, exam: null, deleteQuestionBankToo: false });
        fetchExams();
      } else {
        toast.error(response.data.message || 'Failed to delete exam');
      }
    } catch (error) {
      console.error('Error deleting exam:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.message || 'Cannot delete mock exam. This mock exam has been purchased by one or more parents or students.');
      } else if (error.response?.status === 404) {
        toast.error('Mock exam not found');
        fetchExams();
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error(error.response?.data?.message || 'Failed to delete exam');
      }
    } finally {
      setDeletingExamId(null);
    }
  };

  // Toggle exam status
  const handleToggleStatus = async (exam) => {
    if (!canUpdateMockExams) {
      toast.error('You do not have permission to update mock exams.');
      return;
    }
    if (!exam || !exam.id) {
      toast.error('Cannot toggle status: Exam ID not found');
      return;
    }

    const isActive = isExamActive(exam);
    const action = isActive ? 'deactivate' : 'activate';
    
    console.log('🔄 Toggle Status - Exam ID:', exam.id, '| Status:', exam.status, '| Action:', action);
    
    setTogglingExamId(exam.id);
    
    try {
      const response = await api.patch(`admin/mock-exam/${exam.id}/toggle-status`, {
        action: action
      });
      
      if (response.data.success) {
        toast.success(response.data.message || `Mock exam ${action}d successfully`);
        // Keep the current filters (e.g. Status: Inactive) so the list does not jump back to all.
        fetchExams(true);
      } else {
        toast.error(response.data.message || `Failed to ${action} mock exam`);
      }
    } catch (err) {
      console.error('❌ Error toggling exam status:', err);
      if (err.response?.status === 400) {
        const errors = err.response.data.errors;
        if (errors?.action) {
          toast.error(errors.action[0]);
        } else {
          toast.error(err.response.data.message || 'Invalid action');
        }
      } else if (err.response?.status === 404) {
        toast.error('Mock exam not found');
        fetchExams();
      } else if (err.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error(err.response?.data?.message || 'Failed to toggle mock exam status');
      }
    } finally {
      setTogglingExamId(null);
    }
  };

  // View exam details
  const handleView = async (examId) => {
    if (!canViewMockExams) {
      toast.error('You do not have permission to view mock exams.');
      return;
    }
    try {
      const response = await api.get(`admin/mock-exam/${examId}`);
      const examData = response.data.data;
      console.log('Exam details with questions:', examData);
      setViewingExam(examData);
      setViewDialog(true);
      toast.success('Exam details loaded successfully');
    } catch (error) {
      toast.error('Failed to load exam details');
      console.error('Error loading exam:', error);
    }
  };

  const handleDownloadAnswerKey = async (exam) => {
    if (!canViewMockExams) {
      toast.error('You do not have permission to download answer keys.');
      return;
    }
    setDownloadingAnswerKeyExamId(exam.id);
    try {
      const response = await api.get(`admin/mock-exam/${exam.id}/download-answer-key`, { responseType: 'blob' });
      const contentType = (response.headers?.['content-type'] || '').toLowerCase();
      if (!contentType.includes('application/pdf')) {
        let message = 'Failed to download answer key.';
        try {
          const text = await response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch (_) {}
        toast.error(message);
        return;
      }
      const safeName = (exam?.name || `mock-exam-${exam.id}`)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeName || `mock-exam-${exam.id}`}-answer-key.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Answer key download started');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to download answer key');
    } finally {
      setDownloadingAnswerKeyExamId(null);
    }
  };

  const handleViewPdf = async (exam) => {
    if (!canViewMockExams) {
      toast.error('You do not have permission to view mock exams.');
      return;
    }

    const externalLink = (exam?.original_paper_link || '').trim();
    if (externalLink && /^https?:\/\//i.test(externalLink)) {
      window.open(externalLink, '_blank', 'noopener,noreferrer');
      return;
    }

    setViewingPdfExamId(exam.id);
    try {
      const response = await api.get(`admin/mock-exam/${exam.id}/view-pdf`, { responseType: 'blob' });
      const contentType = (response.headers?.['content-type'] || '').toLowerCase();
      if (!contentType.includes('application/pdf')) {
        let message = 'No PDF is available for this mock exam yet.';
        try {
          const text = await response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch (_) {}
        toast.error(message);
        return;
      }
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to open mock exam PDF');
    } finally {
      setViewingPdfExamId(null);
    }
  };

  // Open dialog for editing
  const handleEdit = async (exam) => {
    if (!canUpdateMockExams) {
      toast.error('You do not have permission to edit mock exams.');
      return;
    }
    try {
      const response = await api.get(`admin/mock-exam/${exam.id}`);
      const examData = response.data.data;
      
      console.log('Edit - API Response:', examData);
      
      // If exam has bank questions, use default empty question (bank questions take priority)
      const hasBankQuestionsInExam = examData.question_bank_questions && examData.question_bank_questions.length > 0;

      // Transform questions from API format to form format
      const transformedQuestions = (!hasBankQuestionsInExam && examData.questions && examData.questions.length > 0)
        ? examData.questions.map((q, index) => {
            // Transform options array to object format { 1: '', 2: '', 3: '', 4: '' }
            const optionsObj = { 1: '', 2: '', 3: '', 4: '' };
            
            if (q.options && Array.isArray(q.options)) {
              // Sort options by order if available, otherwise use array order
              const sortedOptions = [...q.options].sort((a, b) => {
                if (a.order !== undefined && b.order !== undefined) {
                  return a.order - b.order;
                }
                return 0;
              });
              
              // Map options to object format
              sortedOptions.forEach((opt, optIndex) => {
                const key = (optIndex + 1).toString();
                optionsObj[key] = opt.option_text || opt.option || '';
              });
            }
            
            return {
              question: q.question_text || q.question || '',
              options: optionsObj,
              answer: q.correct_answer || q.answer || '',
              duration_in_sec: q.duration_in_sec || q.duration || '',
              marks: q.marks || 1
            };
          })
        : [{
          question: '',
          options: { 1: '', 2: '', 3: '', 4: '' },
          answer: '',
          duration_in_sec: '',
          marks: 1
          }];
      
      // Determine format value - prefer format_id, fallback to format string
      let formatValue = examData.format_id;
      if (!formatValue && examData.format) {
        // If format is a string, try to find matching format in formats array
        if (formats.length > 0) {
          const formatObj = formats.find(f => {
            // Try matching by name
            if (f.name && f.name.toLowerCase() === examData.format.toLowerCase()) {
              return true;
            }
            // Try matching by ID if format string is numeric
            if (!isNaN(examData.format)) {
              const formatId = typeof f.id === 'string' ? parseInt(f.id, 10) : f.id;
              const examFormatId = parseInt(examData.format, 10);
              return formatId === examFormatId;
            }
            return false;
          });
          if (formatObj) {
            formatValue = formatObj.id;
          } else {
            // If not found, use format string as is (will be converted to number if possible)
            formatValue = isNaN(examData.format) ? examData.format : parseInt(examData.format, 10);
          }
        } else {
          formatValue = isNaN(examData.format) ? examData.format : parseInt(examData.format, 10);
        }
      }
      
      const rawTargets = Array.isArray(examData.target_school_ids) ? examData.target_school_ids : [];
      const targetSchoolIds =
        rawTargets.length > 0
          ? normalizeTargetSchoolIds(rawTargets)
          : examData.school_id != null && examData.school_id !== ''
            ? normalizeTargetSchoolIds([examData.school_id])
            : [];

      const formData = {
        name: examData.name || '',
        description: examData.description || '',
        original_paper_link: examData.original_paper_link || '',
        category_id: examData.category_id || '',
        format: formatValue || '',
        subject_ids: Array.isArray(examData.subject_ids)
          ? examData.subject_ids.map((id) => Number(id))
          : Array.isArray(examData.subjects) && examData.subjects.length && typeof examData.subjects[0] === 'object'
            ? examData.subjects.map((s) => Number(s.id))
            : [],
        school_year_id:
          examData.school_year_id != null && examData.school_year_id !== ''
            ? Number(examData.school_year_id)
            : '',
        price: examData.price || '',
        currency: examData.currency && ['€', '$', '£'].includes(examData.currency) ? examData.currency : '£',
        is_free_preview: !!examData.is_free_preview,
        duration_minutes:
          examData.duration_minutes != null && examData.duration_minutes !== ''
            ? Number(examData.duration_minutes)
            : 60,
        held_date: examData.held_date || '',
        held_time: examData.held_time || '',
        due_at: examData.due_at ? examData.due_at.slice(0, 16) : '',
        location_ids: Array.isArray(examData.location_ids) ? examData.location_ids.map((id) => Number(id)) : [],
        target_school_ids: targetSchoolIds,
        max_attempts: examData.max_attempts ?? 2,
        marks_per_correct_answer: examData.marks_per_correct_answer ?? 1,
        negative_marks_penalty: examData.negative_marks_penalty ?? 0,
        instructions: examData.instructions || '',
        questions: transformedQuestions
      };
      
      console.log('Edit - Transformed Form Data:', formData);
      
      // Store existing image URL if available
      const imageUrl = examData.image_url || examData.mock_exam_image_url || examData.image || null;
      setExistingImageUrl(imageUrl);
      
      setEditingExam(examData);

      // Populate question bank questions from the existing exam links
      const bankQuestionsForState = (examData.question_bank_questions || []).map(q => ({
        id: q.id,
        question_text: q.question_text,
        type: q.type,
        difficulty: q.difficulty,
        default_marks: q.marks || 1,
        category_name: q.category_name,
        image_url: q.image_url || null,
      }));
      setSelectedBankQuestions(bankQuestionsForState);

      reset(formData);
      setTabValue(1); // Switch to Create Exam tab for editing
    } catch (error) {
      toast.error('Failed to load exam details');
      console.error('Error loading exam:', error);
      if (error.response?.status === 404) {
        toast.error('Mock exam not found');
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
  };

  // Open create exam tab
  const handleCreate = () => {
    if (!canCreateMockExams) {
      toast.error('You do not have permission to create mock exams.');
      return;
    }
    setEditingExam(null);
    setExistingImageUrl(null);
    reset();
    setTabValue(1); // Switch to Create Exam tab
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    if (!canViewMockExams || !hasCreateTab) {
      return;
    }
    setTabValue(newValue);
  };

  // Add new question
  const addQuestion = () => {
    append({
      question: '',
      options: { 1: '', 2: '', 3: '', 4: '' },
      answer: '',
      duration_in_sec: '',
      marks: 1
    });
  };

  // Auto-scroll to first validation error
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      // Small delay to ensure DOM is updated with error states
      const scrollTimer = setTimeout(() => {
        // Priority order: regular fields first, then question errors
        const regularFieldNames = ['name', 'description', 'category_id', 'format', 'subject_ids', 'school_year_id', 'price', 'currency', 'duration_minutes', 'held_date', 'held_time', 'due_at', 'location_ids', 'target_school_ids', 'mock_exam_image', 'max_attempts', 'marks_per_correct_answer', 'negative_marks_penalty', 'instructions', 'advanced_settings'];
        
        // Find the first regular field with an error using data attributes
        for (const fieldName of regularFieldNames) {
          if (errors[fieldName]) {
            // Use data-field-container attribute to find the Grid item
            const fieldContainer = document.querySelector(`[data-field-container="${fieldName}"]`);
            if (fieldContainer) {
              fieldContainer.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'nearest'
              });
              
              // Try to focus the actual input field within the container
              const inputField = fieldContainer.querySelector('input, textarea, select');
              if (inputField && inputField.focus) {
                try {
                  setTimeout(() => inputField.focus(), 100);
                } catch (e) {
                  // Ignore focus errors
                }
              }
              return;
            }
          }
        }
        
        // If no regular field errors found, check for question errors
        if (errors.questions) {
          // First, try to scroll to the question error summary card
          const errorCard = document.querySelector('[data-question-errors]');
          if (errorCard) {
            errorCard.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
            return;
          }
          
          // If error card not visible, try to find the first question field with error
          if (Array.isArray(errors.questions)) {
            for (let index = 0; index < errors.questions.length; index++) {
              const questionError = errors.questions[index];
              if (questionError) {
                // Try to find question input fields
                const questionWrap = document.querySelector(`[data-question-field-index="${index}"]`);
                const questionField =
                  questionWrap?.querySelector?.('.ql-editor') ||
                  document.querySelector(`input[name="questions.${index}.question"], textarea[name="questions.${index}.question"]`);
                if (questionWrap || questionField) {
                  (questionWrap || questionField).scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest',
                  });
                  try {
                    setTimeout(() => questionField?.focus?.(), 100);
                  } catch (e) {
                    // Ignore focus errors
                  }
                  return;
                }
                
                // Try to find option fields with errors
                if (questionError.options) {
                  for (let optIdx = 1; optIdx <= 4; optIdx++) {
                    if (questionError.options[optIdx]) {
                      const optionField = document.querySelector(`input[name="questions.${index}.options.${optIdx}"]`);
                      if (optionField) {
                        optionField.scrollIntoView({ 
                          behavior: 'smooth', 
                          block: 'center',
                          inline: 'nearest'
                        });
                        try {
                          setTimeout(() => optionField.focus(), 100);
                        } catch (e) {
                          // Ignore focus errors
                        }
                        return;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }, 200);
      
      return () => clearTimeout(scrollTimer);
    }
  }, [errors]);

  useEffect(() => {
    if (!canViewMockExams && showFormTab) {
      setTabValue(1);
    }
  }, [canViewMockExams, showFormTab]);

  // Load initial data once on mount
  useEffect(() => {
    fetchCategories();
    fetchFormats();
    fetchSchoolYears();
    fetchSubjects();
    fetchSchools();
    fetchRegions();
    fetchLocations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canViewMockExams) return;
    const params = new URLSearchParams(location.search || '');
    const next = {
      search: '',
      category_id: findMockExamCategoryId(categories) || '',
      school_category: '',
      school_id: '',
      consortium_id: '',
      exam_school_id: '',
      region_id: '',
      nation: '',
      subject_id: '',
      similarity: '',
      status: '',
    };
    if (params.get('status')) next.status = params.get('status');
    if (params.get('school_id')) next.school_id = params.get('school_id');
    if (params.get('consortium_id')) next.consortium_id = params.get('consortium_id');
    if (params.get('exam_school_id')) next.exam_school_id = params.get('exam_school_id');
    if (params.get('region_id')) next.region_id = params.get('region_id');
    if (params.get('nation')) next.nation = params.get('nation');
    if (params.get('subject_id')) next.subject_id = params.get('subject_id');
    if (params.get('category_id')) next.category_id = params.get('category_id');
    if (params.get('target_school_ids')) next.school_id = params.get('target_school_ids').split(',')[0];
    if (!next.school_id && !next.consortium_id && !next.exam_school_id && !next.region_id && !next.nation && !next.subject_id && !next.status && !params.get('category_id')) return;
    setFilters(next);
    setPage(0);
    fetchExams(true, 0, null, next);
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Default the list to the "Mock Exam" category once categories are loaded
  useEffect(() => {
    if (!canViewMockExams || !categories.length) return;
    const mockExamId = findMockExamCategoryId(categories);
    if (!mockExamId) {
      fetchExams();
      return;
    }
    if (filters.category_id) return;
    const next = { ...filters, category_id: mockExamId };
    setFilters(next);
    fetchExams(true, 0, null, next);
  }, [categories]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-map format names on existing exams whenever formats finish loading
  useEffect(() => {
    if (formats.length === 0) return;
    setExams(prev => prev.map(exam => {
      const mapped = { ...exam };
      if (typeof exam.format === 'string') {
        const formatObj = formats.find(f =>
          (f.name && f.name.toLowerCase() === exam.format.toLowerCase()) ||
          (!isNaN(exam.format) && (typeof f.id === 'string' ? parseInt(f.id, 10) : f.id) === parseInt(exam.format, 10))
        );
        mapped.format_name = formatObj ? formatObj.name : exam.format;
      } else if (exam.format_id) {
        const formatObj = formats.find(f => {
          const fId = typeof f.id === 'string' ? parseInt(f.id, 10) : f.id;
          const eId = typeof exam.format_id === 'string' ? parseInt(exam.format_id, 10) : exam.format_id;
          return fId === eId;
        });
        if (formatObj) mapped.format_name = formatObj.name;
      }
      return mapped;
    }));
  }, [formats]);

  const allConsortiumOptions = useMemo(() => consortiumOptionsForMenu(schools), [schools]);
  const examAreaOptions = useMemo(
    () => examAreaOptionsForMenu(filterSchools, { category: filters.school_category }),
    [filterSchools, filters.school_category]
  );

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      p: 3 
    }}>
      {/* Header Section with Gradient */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        mb: 4 
      }}>
        <Box>
          <Typography component="h1" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#111827', lineHeight: 1.3, mb: 2 }}>
            Mock Exams
          </Typography>
          <Typography variant="body2" sx={{ color: '#5a6c7d', fontSize: '14px', maxWidth: 400 }}>
            Create and manage comprehensive mock exams with multiple choice questions
          </Typography>
        </Box>
        {/* Mock exam bundles (one per area / school) are packs: their price and mocks are edited
            in Papers → Bundle Packs. A single mock's price is edited on the exam itself. */}
        <Button
          component={RouterLink}
          to="/admin/papers?tab=packs&kind=mock_group"
          variant="outlined"
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, whiteSpace: 'nowrap', bgcolor: '#fff' }}
        >
          Mock exam bundles & prices
        </Button>
      </Box>

      {/* Enhanced Tabs — only when user can list exams and create new ones */}
      {canViewMockExams && hasCreateTab ? (
        <Card sx={{ 
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px)'
        }}>
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)',
            borderRadius: '12px 12px 0 0'
          }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  minHeight: { xs: 48, sm: 64 },
                  px: { xs: 1.5, sm: 3 },
                  '&.Mui-selected': {
                    color: '#667eea',
                  }
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                }
              }}
            >
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <QuizIcon />
                    All Exams
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon />
                    Create Exam
                  </Box>
                } 
              />
            </Tabs>
          </Box>
        </Card>
      ) : null}

      {/* Tab Panel 0 - List View */}
      {tabValue === 0 && canViewMockExams && (
        <Box>
          {/* Enhanced Filters Card */}
          <Card sx={{ 
            mb: 4,
            borderRadius: 4,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}>
                  <QuizIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#263238' }}>
                  Filter & Search Exams
                </Typography>
              </Box>
              <Grid container spacing={2}>
              {/* Full-width search with suggestions (29 Oct 2026): exams whose name, school or
                  subject match, as you type. Picking one searches for it; Enter searches the text. */}
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={searchSuggestions}
                  loading={searchSuggestLoading}
                  inputValue={filters.search}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input' || reason === 'clear') setFilters((f) => ({ ...f, search: value }));
                    if (reason === 'clear') {
                      setPage(0);
                      fetchExams(true, 0, null, { ...filters, search: '' });
                    }
                  }}
                  onChange={(_, value) => {
                    const text = typeof value === 'string' ? value : value?.name;
                    if (text == null) return;
                    const next = { ...filters, search: text };
                    setFilters(next);
                    setPage(0);
                    fetchExams(true, 0, null, next);
                  }}
                  value={null}
                  filterOptions={(options) => options}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : o.name || '')}
                  renderOption={(props, o) => (
                    <Box component="li" {...props} key={o.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{o.name}</Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        #{o.id}{o.school_year ? ` · ${o.school_year}` : ''}
                      </Typography>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search exams"
                      placeholder="Type an exam, school or subject name…"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#667eea', fontSize: 26 }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <>
                            {searchSuggestLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      minHeight: 56,
                      fontSize: '1.05rem',
                      borderRadius: 2,
                      backgroundColor: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={filters.category_id}
                    onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}
                    label="Category"
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {flattenCategories(categories).map((cat) => (
                      <MenuItem
                        key={cat.id}
                        value={cat.id}
                        sx={{ pl: 2 + cat.level * 2 }}
                      >
                        {cat.originalName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
                <FormControl fullWidth>
                  <InputLabel>School type</InputLabel>
                  <Select
                    value={filters.school_category || ''}
                    onChange={(e) => {
                      const school_category = e.target.value;
                      const kept = schools.find((school) => String(school.id) === String(filters.school_id));
                      const school_id = kept && schoolMatchesCategory(kept, school_category) ? filters.school_id : '';
                      const area = keptExamArea(filterSchools, filters, { school_category, school_id });
                      setFilters({ ...filters, school_category, school_id, ...area });
                    }}
                    label="School type"
                  >
                    <MenuItem value="">All school types</MenuItem>
                    {SCHOOL_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Subject</InputLabel>
                  <Select
                    value={filters.subject_id}
                    onChange={(e) => setFilters({ ...filters, subject_id: e.target.value })}
                    label="Subject"
                  >
                    <MenuItem value="">All subjects</MenuItem>
                    {subjects.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="">All statuses</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive · testers only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
                <FormControl fullWidth>
                  <InputLabel>Nation</InputLabel>
                  <Select
                    value={filters.nation}
                    onChange={(e) => setFilters({ ...filters, nation: e.target.value })}
                    label="Nation"
                  >
                    <MenuItem value="">All nations</MenuItem>
                    {['England', 'Northern Ireland', 'Scotland', 'Wales'].map((n) => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
                <FormControl fullWidth>
                  <InputLabel>Region</InputLabel>
                  <Select
                    value={filters.region_id}
                    onChange={(e) => setFilters({ ...filters, region_id: e.target.value })}
                    label="Region"
                  >
                    <MenuItem value="">All regions</MenuItem>
                    {regions.filter((r) => !filters.nation || r.nation === filters.nation).map((r) => (
                      <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
                <FormControl fullWidth>
                  <InputLabel>Consortium</InputLabel>
                  <Select
                    value={examAreaSelectValue(filters)}
                    onChange={(e) => setFilters(applyExamAreaToFilters(filters, e.target.value, schools))}
                    label="Consortium"
                    MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}
                  >
                    <MenuItem value="">All consortiums and schools</MenuItem>
                    {examAreaOptions.flatMap((option, index) => {
                      const previous = examAreaOptions[index - 1]?.group || '';
                      const item = (
                        <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                      );
                      if (!option.group || option.group === previous) return [item];
                      return [
                        <MenuItem key={`group-${option.group}`} disabled sx={{ fontWeight: 700, opacity: '1 !important', color: 'text.secondary', bgcolor: '#F8FAFC' }}>
                          {option.group}
                        </MenuItem>,
                        item,
                      ];
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Target school</InputLabel>
                  <Select
                    value={filters.school_id}
                    onChange={(e) => setFilters({ ...filters, school_id: e.target.value })}
                    label="Target school"
                  >
                    <MenuItem value="">All schools</MenuItem>
                    {groupSchoolsForMenu(schools, filters.school_category, filters.consortium_id, {
                      examSchoolId: filters.exam_school_id,
                      regionId: filters.region_id,
                      nation: filters.nation,
                    }).flatMap((group) => [
                      <ListSubheader key={group.label} disableSticky>{group.label}</ListSubheader>,
                      ...group.schools.map((sch) => (
                        <MenuItem key={sch.id} value={sch.id}>{sch.name}</MenuItem>
                      )),
                    ])}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
                <FormControl fullWidth>
                  <InputLabel>Similarity</InputLabel>
                  <Select
                    value={filters.similarity || ''}
                    onChange={(e) => setFilters({ ...filters, similarity: e.target.value })}
                    label="Similarity"
                  >
                    {SIMILARITY_FILTER_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  onClick={() => { setPage(0); fetchExams(true, 0); }}
                  fullWidth
                    sx={{
                      height: 56,
                      borderRadius: 2,
                      borderWidth: 2,
                      borderColor: '#667eea',
                      color: '#667eea',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                      '&:hover': {
                        borderWidth: 2,
                        borderColor: '#764ba2',
                        backgroundColor: 'rgba(102, 126, 234, 0.08)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                >
                  Apply Filters
                </Button>
              </Grid>
              {canCreateMockExams ? (
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                    sx={{
                      ...gradientButtonStyle,
                      height: 56,
                      borderRadius: 2,
                      boxShadow: '0 4px 15px rgba(59, 42, 159, 0.3)',
                      '&:hover': {
                        ...gradientButtonStyle['&:hover'],
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(59, 42, 159, 0.4)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                    fullWidth
                  >
                    Create Exam
                  </Button>
                </Grid>
              ) : null}
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<LanguageIcon />}
                  onClick={() => setBulkWebOpen(true)}
                  sx={{ height: 56, borderRadius: 2, borderWidth: 2, fontWeight: 600, textTransform: 'none', fontSize: '1rem', '&:hover': { borderWidth: 2 } }}
                >
                  Web check all
                </Button>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="text"
                  fullWidth
                  startIcon={<FilterListIcon />}
                  onClick={() => setShowMoreFilters((v) => !v)}
                  aria-expanded={showMoreFilters}
                  sx={{ height: 56, borderRadius: 2, fontWeight: 600, textTransform: 'none', fontSize: '1rem' }}
                >
                  {showMoreFilters ? 'Fewer filters' : `More filters${hiddenFilterCount ? ` · ${hiddenFilterCount} on` : ''}`}
                </Button>
              </Grid>
            </Grid>
            </CardContent>
          </Card>

          {/* Enhanced Exams Table */}
          <Card sx={{ 
            borderRadius: 4,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)',
            overflow: 'hidden'
          }}>
            <SimilarityReportDialog
              open={Boolean(similarityTarget)}
              endpointBase={similarityTarget ? `/admin/mock-exam/${similarityTarget.id}` : null}
              filename={similarityTarget?.name}
              onClose={() => setSimilarityTarget(null)}
            />
            <BulkWebCheckDialog
              open={bulkWebOpen}
              defaultTypes={['mock_exam']}
              onClose={() => setBulkWebOpen(false)}
              onFinished={() => fetchExams(true)}
            />
            {loadError && !loading && <LoadError what="mock exams" error={loadError} onRetry={() => fetchExams(true)} />}
            <TableContainer>
              <Table className="admin-sticky-actions" sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Format</TableCell>
                    <TableCell>Subjects</TableCell>
                    <TableCell>School year</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell align="right">Questions</TableCell>
                    <TableCell align="right">Total Marks</TableCell>
                    <TableCell>Similarity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, index) => (
                      <TableRow key={index}>
                        {[260, 90, 70, 110, 70, 60, 60, 40, 40, 80, 70, 90].map((w, i) => (
                          <TableCell key={i}><Skeleton width={w} height={24} /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : exams.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <QuizIcon sx={{ fontSize: 56, color: '#D1D5DB', mb: 1.5 }} />
                          <Typography variant="h6" sx={{ color: '#374151', mb: 0.5 }}>No mock exams match these filters</Typography>
                          <Typography variant="body2" sx={{ color: '#6B7280' }}>Try removing a filter, or create a new exam.</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    exams.map((exam) => {
                      const image = exam.image_url || exam.mock_exam_image_url || exam.image;
                      const subjects = Array.isArray(exam.subjects) && exam.subjects.length > 0
                        ? exam.subjects
                        : exam.subject ? [exam.subject] : [];
                      const schoolChips = Array.from(new Map(
                        (exam.target_school_ids || [])
                          .map((sid) => {
                            const id = Number(sid);
                            const sch = schools.find((s) => Number(s.id) === id);
                            const label = sch?.name || `#${sid}`;
                            return [label, { id, label }];
                          })
                          .filter(([, row]) => row.id)
                      ).values());
                      const active = isExamActive(exam);
                      return (
                      <TableRow key={exam.id}>
                        <TableCell sx={{ minWidth: 280, maxWidth: 340 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            {image ? (
                              <Box
                                component="img"
                                src={image}
                                alt=""
                                sx={{ width: 40, height: 40, borderRadius: 1.5, objectFit: 'cover', border: '1px solid #E5E7EB', flexShrink: 0 }}
                              />
                            ) : (
                              <Box sx={{ width: 40, height: 40, borderRadius: 1.5, background: '#EDE9FE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <QuizIcon sx={{ color: '#3B2A9F', fontSize: 20 }} />
                              </Box>
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Tooltip title={exam.name} placement="top-start" enterDelay={500}>
                                <Typography sx={{ ...clampSx(2), fontSize: 14, fontWeight: 600, color: '#111827', lineHeight: 1.35 }}>
                                  {exam.name}
                                </Typography>
                              </Tooltip>
                              {(exam.paper_label || exam.bundle_label) && (
                                // Students and parents see this exam renamed to "… Paper N"
                                // (MockExamPublicLabel), and that is the name they quote when
                                // reporting a problem. Show it next to the real name so staff can
                                // map a complaint back to this row; search accepts it too.
                                <Tooltip title={`Shown to students as "${exam.display_name || exam.name}"`} arrow>
                                  <Box component="span" sx={{ display: 'inline-block', mt: 0.5 }}>
                                    <StatusChip tone="amber" label={exam.paper_label || exam.bundle_label} />
                                  </Box>
                                </Tooltip>
                              )}
                              {schoolChips.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {schoolChips.slice(0, 2).map(({ id, label }) => (
                                    <Chip key={id} size="small" label={label} onClick={() => openSchoolAssessments(id)} sx={{ height: 22, fontSize: 12, maxWidth: 220 }} />
                                  ))}
                                  {schoolChips.length > 2 && (
                                    <Tooltip
                                      arrow
                                      title={
                                        <Box sx={{ py: 0.5 }}>
                                          {schoolChips.slice(2).map(({ id, label }) => <div key={id}>{label}</div>)}
                                        </Box>
                                      }
                                    >
                                      <Chip size="small" label={`+${schoolChips.length - 2} more`} sx={{ height: 22, fontSize: 12, fontWeight: 600, background: '#EDE9FE', color: '#3B2A9F' }} />
                                    </Tooltip>
                                  )}
                                </Box>
                              )}
                              {exam.description ? (
                                <Tooltip title={exam.description} placement="bottom-start" enterDelay={500}>
                                  <Typography sx={{ ...clampSx(1), fontSize: 12, color: '#6B7280', mt: 0.25 }}>
                                    {exam.description}
                                  </Typography>
                                </Tooltip>
                              ) : null}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{orDash(exam.category)}</TableCell>
                        <TableCell>
                          {exam.format_name || exam.format
                            ? <StatusChip tone="grey" label={humanize(exam.format_name || exam.format)} />
                            : EMPTY}
                        </TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          {subjects.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {subjects.map((s, idx) => <StatusChip key={`${s}-${idx}`} tone="purple" label={s} />)}
                            </Box>
                          ) : EMPTY}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{orDash(exam.school_year)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                          {formatMoney(exam.price, exam.currency)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {exam.duration_minutes != null && exam.duration_minutes !== '' ? `${exam.duration_minutes} min` : EMPTY}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{exam.questions_count || 0}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{orDash(exam.total_marks || '')}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                            <SimilarityChip job={exam} height={22} fontSize="0.7rem" onClick={() => setSimilarityTarget(exam)} />
                            <WebSimilarityChip job={exam} height={22} fontSize="0.7rem" onClick={() => setSimilarityTarget(exam)} />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={active ? '' : 'Hidden from students, parents and visitors. Tester accounts still see it, marked as a tester preview, so it can be checked before release.'} arrow>
                            <span><StatusChip tone={active ? 'green' : 'grey'} label={active ? 'Active' : 'Inactive · testers only'} /></span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <RowActions
                            actions={[
                              { label: 'View details', icon: <ViewIcon fontSize="small" />, onClick: () => handleView(exam.id), show: canViewMockExams },
                              { label: 'Edit exam', icon: <EditIcon fontSize="small" />, onClick: () => handleEdit(exam), show: canUpdateMockExams },
                              { label: 'View PDF', icon: <PictureAsPdfIcon />, onClick: () => handleViewPdf(exam), loading: viewingPdfExamId === exam.id, show: canViewMockExams },
                              { label: 'Download answer key', icon: <DownloadIcon />, onClick: () => handleDownloadAnswerKey(exam), loading: downloadingAnswerKeyExamId === exam.id, show: canViewMockExams },
                              { label: active ? 'Deactivate' : 'Activate', icon: active ? <ToggleOffIcon /> : <ToggleOnIcon />, onClick: () => handleToggleStatus(exam), loading: togglingExamId === exam.id, show: canUpdateMockExams },
                              { label: 'Delete exam', icon: <DeleteIcon />, onClick: () => handleDeleteClick(exam), loading: deletingExamId === exam.id, show: canDeleteMockExams, danger: true },
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalExams}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{
                borderTop: '1px solid rgba(102, 126, 234, 0.15)',
                '& .MuiTablePagination-toolbar': { px: 2 },
              }}
            />
          </Card>
        </Box>
      )}

      {/* Tab Panel 1 - Create Form */}
      {tabValue === 1 && showFormTab && (
        <Box>
          {canViewMockExams && !hasCreateTab ? (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setTabValue(0);
                  setEditingExam(null);
                  setExistingImageUrl(null);
                  reset();
                }}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                ← Back to exam list
              </Button>
            </Box>
          ) : null}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Card sx={{ 
              mb: 3,
              borderRadius: 4,
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(20px)'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: '#263238', 
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <QuizIcon sx={{ color: '#667eea' }} />
                  Basic Exam Information
                </Typography>
                <Grid container spacing={3}>
              {/* Basic Exam Info */}
                  <Grid item xs={12} sm={6} data-field-container="name">
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Exam Name"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                            }
                          }}
                    />
                  )}
                />
              </Grid>
              
                  <Grid item xs={12} sm={6} data-field-container="category_id">
                <Controller
                  name="category_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.category_id}>
                      <InputLabel>Category</InputLabel>
                      <Select
                        {...field}
                        value={field.value || ''}
                        label="Category"
                            sx={{
                              borderRadius: 2,
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#667eea',
                              },
                            }}
                      >
                        {flattenCategories(categories).map((cat) => (
                          <MenuItem
                            key={cat.id}
                            value={cat.id}
                            sx={{
                              color: '#263238',
                              fontWeight: 400,
                              fontSize: '0.95rem',
                              pl: 2 + cat.level * 2,
                              py: 1,
                            }}
                          >
                            {cat.originalName}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography color="error" fontSize={12}>
                        {errors.category_id?.message}
                      </Typography>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field-container="format">
                <Controller
                  name="format"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.format}>
                      <InputLabel>Format</InputLabel>
                      <Select {...field} value={field.value || ''} label="Format">
                        {formats.map((format) => (
                          <MenuItem key={format.id} value={format.id}>
                            {format.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography color="error" fontSize={12}>
                        {errors.format?.message}
                      </Typography>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field-container="subject_ids">
                <Controller
                  name="subject_ids"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.subject_ids}>
                      <InputLabel>Subjects</InputLabel>
                      <Select
                        {...field}
                        multiple
                        value={Array.isArray(field.value) ? field.value : []}
                        onChange={(e) => {
                          const v = e.target.value;
                          field.onChange(typeof v === 'string' ? v.split(',').map(Number) : v);
                        }}
                        input={<OutlinedInput label="Subjects" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected || []).map((id) => {
                              const s = subjects.find((x) => Number(x.id) === Number(id));
                              return (
                                <Chip key={id} label={s?.name ?? id} size="small" />
                              );
                            })}
                          </Box>
                        )}
                        label="Subjects"
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#667eea',
                          },
                        }}
                      >
                        {subjects.map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography color="error" fontSize={12}>
                        {errors.subject_ids?.message}
                      </Typography>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field-container="school_year_id">
                <Controller
                  name="school_year_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.school_year_id}>
                      <InputLabel>School year</InputLabel>
                      <Select
                        {...field}
                        value={field.value ?? ''}
                        label="School year"
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#667eea',
                          },
                        }}
                      >
                        {schoolYears.map((y) => (
                          <MenuItem key={y.id} value={y.id}>
                            {y.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography color="error" fontSize={12}>
                        {errors.school_year_id?.message}
                      </Typography>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field-container="price">
                <Controller
                  name="price"
                  control={control}
                  render={({ field }) => {
                    const priceLocked = Boolean(editingExam) && !canChangeProductPrice();
                    return (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      label="Price"
                      disabled={priceLocked}
                      inputProps={{ step: "0.01", min: "0" }}
                      error={!!errors.price}
                      helperText={errors.price?.message || (priceLocked ? "Only an Admin or Super Admin can change the price." : editingExam ? stripePriceNote(editingExam) : undefined)}
                    />
                    );
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field-container="currency">
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.currency}>
                      <InputLabel>Currency (optional — default £)</InputLabel>
                      <Select
                        {...field}
                        value={field.value || '£'}
                        label="Currency (optional — default £)"
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#667eea',
                          },
                        }}
                      >
                        <MenuItem value="£">£ (Pound)</MenuItem>
                        <MenuItem value="€">€ (Euro)</MenuItem>
                        <MenuItem value="$">$ (Dollar)</MenuItem>
                      </Select>
                      <Typography color="error" fontSize={12}>
                        {errors.currency?.message}
                      </Typography>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} data-field-container="is_free_preview">
                <Controller
                  name="is_free_preview"
                  control={control}
                  render={({ field }) => (
                    <FormControl error={!!errors.is_free_preview}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Free preview for web users (max 2 mock exams; everything else is £1.00)"
                      />
                      <Typography color="error" fontSize={12}>
                        {errors.is_free_preview?.message}
                      </Typography>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field-container="duration_minutes">
                <Controller
                  name="duration_minutes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      label="Duration (minutes, optional — default 60)"
                      inputProps={{ min: "1" }}
                      error={!!errors.duration_minutes}
                      helperText={errors.duration_minutes?.message || 'Leave blank to use 60 minutes'}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field-container="held_date">
                <Controller
                  name="held_date"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="date"
                      label="Held date (optional)"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.held_date}
                      helperText={errors.held_date?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field-container="held_time">
                <Controller
                  name="held_time"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="time"
                      label="Held time (optional)"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.held_time}
                      helperText={errors.held_time?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} data-field-container="due_at">
                <Controller
                  name="due_at"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="datetime-local"
                      label="Submission deadline (optional)"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.due_at}
                      helperText={errors.due_at?.message || 'Reminder sent 2 days before this date'}
                    />
                  )}
                />
              </Grid>

              {(() => {
                const fmtId = watch('format');
                const fmt = formats.find((f) => Number(f.id) === Number(fmtId));
                const isPhysical = !!fmt?.name && String(fmt.name).toLowerCase().includes('physical');
                return isPhysical;
              })() ? (
                <Grid item xs={12} sm={6} data-field-container="location_ids">
                  <Controller
                    name="location_ids"
                    control={control}
                    render={({ field }) => {
                      const selected = Array.isArray(field.value) ? field.value.map(Number) : [];
                      return (
                        <FormControl fullWidth error={!!errors.location_ids}>
                          <InputLabel id="mock-exam-locations-label">Locations *</InputLabel>
                          <Select
                            labelId="mock-exam-locations-label"
                            multiple
                            value={selected}
                            onChange={(e) => {
                              const next = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                              field.onChange(next.map(Number));
                            }}
                            input={<OutlinedInput label="Locations *" />}
                            renderValue={(sel) => {
                              if (!sel || sel.length === 0) return 'Select locations…';
                              return sel
                                .map((id) => locations.find((l) => Number(l.id) === Number(id))?.name || id)
                                .join(', ');
                            }}
                            label="Locations *"
                          >
                            {locations.map((loc) => {
                              const lid = Number(loc.id);
                              return (
                                <MenuItem key={loc.id} value={lid}>
                                  <Checkbox checked={selected.includes(lid)} />
                                  <ListItemText primary={loc.name} />
                                </MenuItem>
                              );
                            })}
                          </Select>
                          <Typography color="error" fontSize={12}>
                            {errors.location_ids?.message}
                          </Typography>
                        </FormControl>
                      );
                    }}
                  />
                </Grid>
              ) : null}

              <Grid item xs={12} sm={6} data-field-container="target_school_ids">
                <Controller
                  name="target_school_ids"
                  control={control}
                  render={({ field }) => (
                    <>
                    <Autocomplete
                      multiple
                      options={schools}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                      value={schools.filter((s) =>
                        (Array.isArray(field.value) ? field.value : []).map(Number).includes(Number(s.id))
                      )}
                      onChange={(e, value) => field.onChange((value || []).map((s) => Number(s.id)))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Target schools (optional)"
                          placeholder="Search schools"
                          error={!!errors.target_school_ids}
                          helperText={errors.target_school_ids?.message}
                        />
                      )}
                    />
                    <TextField
                      select
                      size="small"
                      label="Add all schools in a consortium"
                      value=""
                      onChange={(e) => {
                        const members = schools
                          .filter((s) => String(s.consortium_id) === String(e.target.value))
                          .map((s) => Number(s.id));
                        field.onChange(Array.from(new Set([...(field.value || []).map(Number), ...members])));
                      }}
                      sx={{ mt: 1.5, minWidth: 320 }}
                    >
                      {allConsortiumOptions.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name} ({c.count} schools)
                        </MenuItem>
                      ))}
                    </TextField>
                    </>
                  )}
                />
              </Grid>

              <Grid item xs={12} data-field-container="description">
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={3}
                      label="Description"
                      error={!!errors.description}
                      helperText={errors.description?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="original_paper_link"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Original Paper Link"
                      placeholder="https://..."
                      error={!!errors.original_paper_link}
                      helperText={errors.original_paper_link?.message || 'Optional source URL of the original paper'}
                    />
                  )}
                />
              </Grid>

                  {/* Enhanced Mock Exam Image Upload */}
                  <Grid item xs={12} data-field-container="mock_exam_image">
                    <Paper sx={{ 
                      p: 3, 
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #fff8f0 0%, #fef3e8 100%)',
                      border: '1px solid rgba(255, 152, 0, 0.1)'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <CloudUploadIcon sx={{ color: '#ff9800', fontSize: 24 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#263238' }}>
                          Exam Image (Optional)
                        </Typography>
                      </Box>
                <Controller
                  name="mock_exam_image"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <Box>
                      <Box sx={{ 
                        border: '2px dashed',
                              borderColor: errors.mock_exam_image ? 'error.main' : '#ff9800',
                              borderRadius: 3,
                              p: 4,
                        textAlign: 'center',
                              background: 'white',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        '&:hover': {
                                borderColor: '#f57c00',
                                bgcolor: 'rgba(255, 152, 0, 0.04)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 20px rgba(255, 152, 0, 0.15)'
                        },
                        transition: 'all 0.3s ease'
                      }}>
                        <input
                          {...field}
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                                  if (file) {
                            onChange(file);
                                    setExistingImageUrl(null); // Clear existing image when new one is selected
                                  }
                          }}
                          style={{ display: 'none' }}
                          id="mock-exam-image-upload"
                        />
                        <label htmlFor="mock-exam-image-upload">
                          <Button
                                  variant="contained"
                            component="span"
                            startIcon={<CloudUploadIcon />}
                            sx={{
                                    background: 'linear-gradient(45deg, #ff9800, #f57c00)',
                                    color: 'white',
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                              '&:hover': {
                                      background: 'linear-gradient(45deg, #f57c00, #e65100)',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 6px 16px rgba(255, 152, 0, 0.4)'
                                    },
                                    transition: 'all 0.3s ease'
                            }}
                          >
                                  {existingImageUrl ? 'Change Image' : 'Choose Image'}
                          </Button>
                        </label>
                              {/* Show existing image if in edit mode and no new file selected */}
                              {existingImageUrl && !value && (
                                <Box sx={{ mt: 3 }}>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    mb: 2 
                                  }}>
                                    <Box sx={{
                                      position: 'relative',
                                      borderRadius: 3,
                                      overflow: 'hidden',
                                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                                      border: '3px solid',
                                      borderColor: '#2196f3'
                                    }}>
                                      <img
                                        src={existingImageUrl}
                                        alt="Current exam"
                                        style={{
                                          maxWidth: '250px',
                                          maxHeight: '180px',
                                          display: 'block'
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                  <Chip
                                    label="📷 Current Image"
                                    sx={{
                                      background: 'linear-gradient(45deg, #2196f3, #1976d2)',
                                      color: 'white',
                                      fontWeight: 600,
                                      mb: 1
                                    }}
                                  />
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mb: 1 }}>
                                    Upload a new image to replace the current one
                                  </Typography>
                                </Box>
                              )}
                              {/* Show new file preview if a new file is selected */}
                        {value && (
                                <Box sx={{ mt: 3 }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              mb: 2 
                                  }}>
                                    <Box sx={{
                                      position: 'relative',
                                      borderRadius: 3,
                                      overflow: 'hidden',
                                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                                      border: '3px solid',
                                      borderColor: '#4caf50'
                            }}>
                              <img
                                src={URL.createObjectURL(value)}
                                alt="Preview"
                                style={{
                                          maxWidth: '250px',
                                          maxHeight: '180px',
                                          display: 'block'
                                }}
                              />
                            </Box>
                                  </Box>
                                  <Chip
                                    label={`✅ ${value.name} (${(value.size / 1024 / 1024).toFixed(2)} MB)`}
                                    sx={{
                                      background: 'linear-gradient(45deg, #4caf50, #2e7d32)',
                                      color: 'white',
                                      fontWeight: 600,
                                      mb: 1
                                    }}
                                  />
                                  <Box>
                            <Button
                              size="small"
                                      variant="outlined"
                              color="error"
                                      onClick={() => {
                                        onChange(null);
                                        // Restore existing image if available
                                        if (existingImageUrl) {
                                          setExistingImageUrl(existingImageUrl);
                                        }
                                      }}
                                      sx={{ 
                                        mt: 1,
                                        borderRadius: 2,
                                        borderWidth: 2
                                      }}
                            >
                                      Remove New Image
                            </Button>
                                  </Box>
                          </Box>
                        )}
                              <Typography variant="body2" color="text.secondary" mt={2} sx={{ fontSize: '0.875rem' }}>
                          Upload an image for your exam (Max: 10MB, Formats: JPG, PNG, GIF, etc.)
                        </Typography>
                      {errors.mock_exam_image && (
                                <Typography color="error" fontSize={14} mt={2} sx={{ fontWeight: 500 }}>
                          {errors.mock_exam_image.message}
                        </Typography>
                      )}
                            </Box>
                    </Box>
                  )}
                />
                    </Paper>
              </Grid>

              {/* ======= Advanced Settings ======= */}
              <Grid item xs={12} data-field-container="advanced_settings">
                <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <SettingsIcon sx={{ color: '#666' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#263238' }}>
                      Advanced Settings
                    </Typography>
                  </Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4} data-field-container="max_attempts">
                      <Controller
                        name="max_attempts"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            type="number"
                            label="Retake Attempts"
                            inputProps={{ min: 0 }}
                            error={!!errors.max_attempts}
                            helperText={errors.max_attempts?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} data-field-container="marks_per_correct_answer">
                      <Controller
                        name="marks_per_correct_answer"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            type="number"
                            label="Marks per Correct Answer"
                            inputProps={{ min: 0, step: '0.01' }}
                            error={!!errors.marks_per_correct_answer}
                            helperText={errors.marks_per_correct_answer?.message || 'Points awarded for each correct answer.'}
                            FormHelperTextProps={{ sx: { color: errors.marks_per_correct_answer ? undefined : 'text.secondary' } }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} data-field-container="negative_marks_penalty">
                      <Controller
                        name="negative_marks_penalty"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            type="number"
                            label="Negative Marks Penalty"
                            inputProps={{ min: 0, step: '0.01' }}
                            error={!!errors.negative_marks_penalty}
                            helperText={errors.negative_marks_penalty?.message || 'Penalty applied for each incorrect answer.'}
                            FormHelperTextProps={{ sx: { color: errors.negative_marks_penalty ? undefined : 'text.secondary' } }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} data-field-container="instructions">
                      <Controller
                        name="instructions"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            multiline
                            rows={3}
                            label="Exam Instructions"
                            placeholder="Default Instructions (Default)"
                            error={!!errors.instructions}
                            helperText="Choose which instructions will be shown to students before they start the exam."
                            FormHelperTextProps={{ sx: { color: 'text.secondary' } }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* ======= Question Bank Section ======= */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'rgba(63,43,150,0.2)', background: 'linear-gradient(135deg, rgba(63,43,150,0.04) 0%, rgba(167,29,49,0.04) 100%)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <QuizIcon sx={{ color: '#3f2b96' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#263238' }}>
                        Questions from Question Bank
                      </Typography>
                      {selectedBankQuestions.length > 0 && (
                        <Chip label={`${selectedBankQuestions.length} selected`} size="small" color="primary" />
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => { setBankPickerOpen(true); setBankPickerSelected([]); }}
                      sx={{ background: 'linear-gradient(to right, #3f2b96, #a71d31)', textTransform: 'none', fontWeight: 600 }}
                    >
                      Select Questions
                    </Button>
                  </Box>

                  {selectedBankQuestions.length > 0 && (() => {
                    const getQMarks = (q) => parseInt(q.default_marks, 10) || 1;
                    const totalMarks = selectedBankQuestions.reduce((s, q) => s + getQMarks(q), 0);
                    const over = totalMarks > 100;
                    const exact = totalMarks === 100;
                    const remaining = 100 - totalMarks;
                    return (
                      <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: over ? 'error.main' : exact ? 'success.main' : 'divider', bgcolor: over ? 'rgba(244,67,54,0.04)' : exact ? 'rgba(76,175,80,0.04)' : 'background.paper' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75, flexWrap: 'wrap', gap: 1 }}>
                          <Typography variant="caption" fontWeight={600} color={over ? 'error.main' : 'text.secondary'}>
                            Total marks — {selectedBankQuestions.length} question{selectedBankQuestions.length !== 1 ? 's' : ''}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" fontWeight={700} color={over ? 'error.main' : exact ? 'success.main' : 'text.primary'}>
                              {totalMarks} / 100
                              {over ? ' — exceeds 100!' : exact ? ' ✓' : ` (${remaining} more needed)`}
                            </Typography>
                            {!exact && (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={scalingMarks || totalMarks === 0}
                                onClick={handleScaleTo100}
                                sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25, px: 1, minWidth: 'auto', borderColor: over ? 'error.main' : 'primary.main', color: over ? 'error.main' : 'primary.main' }}
                              >
                                {scalingMarks ? <CircularProgress size={12} /> : 'Scale to 100'}
                              </Button>
                            )}
                          </Box>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min((totalMarks / 100) * 100, 100)}
                          color={over ? 'error' : totalMarks >= 90 ? 'warning' : 'primary'}
                          sx={{ borderRadius: 1, height: 8 }}
                        />
                      </Box>
                    );
                  })()}

                  {selectedBankQuestions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No questions selected from bank. Click &ldquo;Select Questions&rdquo; to add.
                    </Typography>
                  ) : (
                    <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleMockDragEnd}>
                      <SortableContext items={selectedBankQuestions.map((q) => String(q.id))} strategy={verticalListSortingStrategy}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {(() => {
                        let lastSectionTitle = null;
                        return selectedBankQuestions.map((q, idx) => {
                          const isNewSection = (q.section_title || null) !== lastSectionTitle;
                          lastSectionTitle = q.section_title || null;
                          const sectionHeader = isNewSection && q.section_title ? (
                            <Box
                              onClick={() => setMockSectionEdit({ qId: q.id, title: q.section_title, instructions: q.section_instructions || '' })}
                              sx={{ px: 1.5, py: 0.75, mb: 0.5, bgcolor: alpha('#1565c0', 0.06), borderRadius: 1, borderLeft: '3px solid #1565c0', cursor: 'pointer', '&:hover': { bgcolor: alpha('#1565c0', 0.12) } }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#1565c0', textTransform: 'uppercase' }}>
                                {q.section_title}
                                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary', fontWeight: 400, textTransform: 'none' }}>(click to edit)</Typography>
                              </Typography>
                              {q.section_instructions && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}>
                                  {q.section_instructions}
                                </Typography>
                              )}
                            </Box>
                          ) : null;
                          return (
                            <SortableMockQuestionRow key={q.id} id={String(q.id)}>
                              {({ dragHandleProps }) => (
                              <React.Fragment>
                                {mockSectionEdit?.qId === q.id && (
                                  <Box sx={{ px: 1.5, py: 1.5, mb: 0.5, bgcolor: alpha('#1565c0', 0.06), borderRadius: 1, borderLeft: '3px solid #1565c0', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#1565c0', textTransform: 'uppercase' }}>
                                      Section break before Q{idx + 1}
                                    </Typography>
                                    <TextField
                                      size="small"
                                      autoFocus
                                      label="Section title"
                                      value={mockSectionEdit.title}
                                      onChange={(e) => setMockSectionEdit((s) => ({ ...s, title: e.target.value }))}
                                      onKeyDown={(e) => { if (e.key === 'Escape') setMockSectionEdit(null); }}
                                      placeholder="e.g. Section A"
                                      sx={{ bgcolor: 'white' }}
                                    />
                                    <TextField
                                      size="small"
                                      label="Section instructions"
                                      multiline
                                      rows={2}
                                      value={mockSectionEdit.instructions}
                                      onChange={(e) => setMockSectionEdit((s) => ({ ...s, instructions: e.target.value }))}
                                      onKeyDown={(e) => { if (e.key === 'Escape') setMockSectionEdit(null); }}
                                      placeholder="Instructions shown at the start of this section (optional)"
                                      sx={{ bgcolor: 'white' }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                      <Button size="small" disabled={mockSectionSaving} onClick={() => setMockSectionEdit(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
                                      {q.section_title && (
                                        <Button size="small" variant="outlined" color="error" disabled={mockSectionSaving} onClick={async () => { try { await api.put(`/admin/question-bank/${q.id}`, { section_title: null, section_instructions: null }); setSelectedBankQuestions((prev) => prev.map((bq) => bq.id === q.id ? { ...bq, section_title: null, section_instructions: null } : bq)); setMockSectionEdit(null); } catch { toast.error('Failed to remove section'); } }} sx={{ textTransform: 'none' }}>Remove break</Button>
                                      )}
                                      <Button size="small" variant="contained" disabled={mockSectionSaving} onClick={saveMockSectionEdit} sx={{ textTransform: 'none' }}>Save</Button>
                                    </Box>
                                  </Box>
                                )}
                                {sectionHeader}
                                <Paper key={q.id} variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 24, color: 'text.secondary' }}>
                                    {idx + 1}.
                                  </Typography>
                                  {q.image_url && (
                                    <Box sx={{ flexShrink: 0 }}>
                                      <img
                                        src={q.image_url}
                                        alt="Question"
                                        style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid #e0e0e0' }}
                                      />
                                    </Box>
                                  )}
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ mb: 0.5, maxHeight: 130, overflow: 'auto' }}>
                                      <HtmlWithLatex html={q.question_text || ''} />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Chip label={q.type?.replace(/-/g, ' ') || 'MCQ'} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                      <Chip label={q.difficulty || 'Medium'} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                      <Chip label={`${q.default_marks || 1} mark${(q.default_marks || 1) !== 1 ? 's' : ''}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
                                    <Tooltip title="Drag to reorder" arrow>
                                      <IconButton size="small" sx={{ color: '#bdbdbd', cursor: 'grab' }} {...dragHandleProps}>
                                        <DragIndicatorIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Move up" arrow>
                                      <span>
                                        <IconButton size="small" onClick={() => moveBankQuestion(idx, 'up')} disabled={idx === 0} sx={{ color: '#5c6bc0' }}>
                                          <ArrowUpIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Move down" arrow>
                                      <span>
                                        <IconButton size="small" onClick={() => moveBankQuestion(idx, 'down')} disabled={idx === selectedBankQuestions.length - 1} sx={{ color: '#5c6bc0' }}>
                                          <ArrowDownIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title={q.section_title ? `Edit section: "${q.section_title}"` : 'Add section break before this question'} arrow>
                                      <IconButton size="small" onClick={() => setMockSectionEdit({ qId: q.id, title: q.section_title || '', instructions: q.section_instructions || '' })} sx={{ color: q.section_title ? '#1565c0' : alpha('#1565c0', 0.5) }}>
                                        <SectionIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit question" arrow>
                                      <IconButton size="small" color="primary" onClick={() => openBankQuestionEdit(q)}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Remove" arrow>
                                      <IconButton size="small" color="error" onClick={() => removeBankQuestion(q.id)}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Paper>
                              </React.Fragment>
                              )}
                            </SortableMockQuestionRow>
                          );
                        });
                      })()}
                    </Box>
                      </SortableContext>
                    </DndContext>
                  )}
                </Paper>
              </Grid>

              {/* CSV Upload Section — only shown when no bank questions selected */}
               {selectedBankQuestions.length === 0 && <Grid item xs={12}>
                 <Box sx={{
                   p: 4,
                   background: 'linear-gradient(135deg, rgba(2, 136, 209, 0.08) 0%, rgba(21, 101, 192, 0.12) 100%)',
                   borderRadius: 3,
                   border: '2px dashed',
                   borderColor: 'info.main',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                    animation: 'shimmer 3s infinite',
                  },
                  '@keyframes shimmer': {
                    '0%': { left: '-100%' },
                    '100%': { left: '100%' },
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(2, 136, 209, 0.15)',
                    borderColor: 'info.dark',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}>
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h5" mb={2} sx={{ 
                      color: 'info.dark', 
                      fontWeight: 700,
                      background: 'linear-gradient(45deg, #0288d1, #1565c0)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2
                    }}>
                      <Box sx={{
                        fontSize: '2rem',
                        animation: 'bounce 2s infinite',
                        '@keyframes bounce': {
                          '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
                          '40%': { transform: 'translateY(-10px)' },
                          '60%': { transform: 'translateY(-5px)' },
                        }
                      }}>
                        📤
                      </Box>
                      Bulk Question Upload
                    </Typography>
                    <Typography variant="body1" mb={4} sx={{ 
                      color: 'info.dark',
                      fontWeight: 500,
                      opacity: 0.9
                    }}>
                      Upload multiple questions at once using CSV format for faster exam creation
                    </Typography>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={3} flexWrap="wrap">
                      <Button 
                        variant="contained" 
                        component="label"
                        size="large"
                        sx={{
                          background: 'linear-gradient(45deg, #0288d1 30%, #1565c0 90%)',
                          boxShadow: '0 4px 15px rgba(2, 136, 209, 0.3)',
                          px: 4,
                          py: 1.5,
                          borderRadius: 3,
                          fontWeight: 600,
                          fontSize: '1rem',
                          '&:hover': { 
                            background: 'linear-gradient(45deg, #0277bd 30%, #1565c0 90%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(2, 136, 209, 0.4)',
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        📁 Upload CSV File
                        <input type="file" hidden accept=".csv" onChange={handleCSVUpload} />
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        sx={{ 
                          borderColor: 'info.main',
                          color: 'info.main',
                          px: 4,
                          py: 1.5,
                          borderRadius: 3,
                          fontWeight: 600,
                          borderWidth: 2,
                          '&:hover': { 
                            borderColor: 'info.dark',
                            bgcolor: 'rgba(2, 136, 209, 0.08)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 15px rgba(2, 136, 209, 0.2)',
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                        onClick={() => {
                          const sample = `question,option1,option2,option3,option4,answer,duration_in_sec,marks
What is the capital of France?,Paris,Lyon,Marseille,Nice,Paris,30,1
What is 2+2?,3,4,5,6,4,20,1`;
                          const blob = new Blob([sample], { type: 'text/csv;charset=utf-8;' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', 'sample_mock_exam_format.csv');
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                         📥 Download Sample
                       </Button>
                     </Box>
                    {(csvImportState.fileName || csvImportState.message) && (
                      <Box sx={{ mt: 3, maxWidth: 560, mx: 'auto', textAlign: 'left' }}>
                        {csvImportState.loading ? (
                          <Alert
                            severity="info"
                            icon={<CircularProgress size={18} color="inherit" />}
                            sx={{ alignItems: 'center' }}
                          >
                            <Typography variant="body2" fontWeight={600}>
                              {csvImportState.fileName ? `Importing: ${csvImportState.fileName}` : 'Importing…'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Parsing CSV — this usually takes a moment.
                            </Typography>
                          </Alert>
                        ) : (
                          <Alert
                            severity={csvImportState.severity === 'success' ? 'success' : csvImportState.severity === 'error' ? 'error' : 'info'}
                            onClose={() =>
                              setCsvImportState({ loading: false, fileName: '', message: '', severity: 'info' })
                            }
                          >
                            {csvImportState.fileName && (
                              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                {csvImportState.severity === 'success' ? 'Imported' : 'File'}: {csvImportState.fileName}
                              </Typography>
                            )}
                            {csvImportState.message && (
                              <Typography variant="body2">{csvImportState.message}</Typography>
                            )}
                          </Alert>
                        )}
                      </Box>
                    )}
                   </Box>
                 </Box>
               </Grid>}

                  {/* Enhanced CSV Format Preview — only shown when no bank questions selected */}
              {selectedBankQuestions.length === 0 && <Grid item xs={12}>
                    <Card sx={{ 
                  mt: 2, 
                      borderRadius: 3,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                      background: 'rgba(255,255,255,0.95)',
                  border: '1px solid',
                      borderColor: 'rgba(2, 136, 209, 0.2)'
                }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                          <Box sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #0288d1 0%, #1565c0 100%)',
                    display: 'flex', 
                    alignItems: 'center', 
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(2, 136, 209, 0.3)'
                          }}>
                            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.2rem' }}>📊</Typography>
                          </Box>
                          <Typography variant="h6" sx={{ 
                            color: '#263238',
                    fontWeight: 600
                  }}>
                            CSV Format Preview
                  </Typography>
                        </Box>
                        <TableContainer component={Paper} sx={{ 
                          borderRadius: 3, 
                          overflow: 'hidden',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}>
                    <Table size="small">
                      <TableHead>
                              <TableRow sx={{ background: 'linear-gradient(135deg, #0288d1 0%, #1565c0 100%)' }}>
                          {['question', 'option1', 'option2', 'option3', 'option4', 'answer', 'duration_in_sec', 'marks'].map(header => (
                            <TableCell key={header} sx={{ 
                              color: 'white', 
                              fontWeight: 600,
                              fontSize: '0.875rem'
                            }}>
                              {header}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                              <TableRow sx={{ '&:hover': { bgcolor: 'rgba(2, 136, 209, 0.05)' }, transition: 'all 0.2s' }}>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 500 }}>What is the capital of France?</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>Paris</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>Lyon</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>Marseille</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>Nice</TableCell>
                                <TableCell sx={{ 
                                  fontFamily: 'monospace', 
                                  fontSize: '0.85rem', 
                                  color: '#2e7d32', 
                                  fontWeight: 700,
                                  background: 'rgba(76, 175, 80, 0.1)'
                                }}>
                                  Paris
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>30</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>1</TableCell>
                        </TableRow>
                              <TableRow sx={{ '&:hover': { bgcolor: 'rgba(2, 136, 209, 0.05)' }, transition: 'all 0.2s' }}>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 500 }}>What is 2+2?</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>3</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>4</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>5</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>6</TableCell>
                                <TableCell sx={{ 
                                  fontFamily: 'monospace', 
                                  fontSize: '0.85rem', 
                                  color: '#2e7d32', 
                                  fontWeight: 700,
                                  background: 'rgba(76, 175, 80, 0.1)'
                                }}>
                                  4
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>30</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>1</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                       </CardContent>
                     </Card>
              </Grid>}

              {/* Questions Section — only shown when no bank questions selected */}
              {selectedBankQuestions.length === 0 && fields.map((field, idx) => (
                <React.Fragment key={field.id}>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      mt: 4, 
                      mb: 3, 
                      p: 3, 
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: '0 8px 32px rgba(25, 118, 210, 0.3)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
                        animation: 'slideGlow 2s ease-in-out infinite',
                      },
                      '@keyframes slideGlow': {
                        '0%': { transform: 'translateX(-100%)' },
                        '100%': { transform: 'translateX(100%)' },
                      },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 40px rgba(25, 118, 210, 0.4)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}>
                      <Box sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: 'linear-gradient(45deg, #ffffff 0%, #f5f5f5 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'primary.main',
                        fontWeight: 800,
                        fontSize: '1.2rem',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: -2,
                          left: -2,
                          right: -2,
                          bottom: -2,
                          borderRadius: '50%',
                          background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.3), transparent)',
                          zIndex: -1,
                        }
                      }}>
                        {idx + 1}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" sx={{ 
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '1.4rem',
                          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                          letterSpacing: '0.5px'
                        }}>
                          Question {idx + 1}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontWeight: 400,
                          mt: 0.5
                        }}>
                          Fill in the question details below
                        </Typography>
                      </Box>
                      {fields.length > 1 && (
                        <Button
                          variant="outlined"
                          size="medium"
                          onClick={() => remove(idx)}
                          startIcon={<DeleteIcon />}
                          sx={{
                            color: 'white',
                            borderColor: 'rgba(255, 255, 255, 0.7)',
                            borderWidth: 2,
                            px: 3,
                            py: 1,
                            borderRadius: 2,
                            fontWeight: 600,
                            '&:hover': {
                              borderColor: 'white',
                              bgcolor: 'rgba(255, 255, 255, 0.15)',
                              transform: 'scale(1.05)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                            },
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} data-question-field-index={idx}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
                      Question *
                    </Typography>
                    <Controller
                      name={`questions.${idx}.question`}
                      control={control}
                      render={({ field }) => (
                        <Box
                          sx={{
                            '& .quill': { background: '#fff', borderRadius: 1 },
                            '& .ql-container': {
                              minHeight: '150px',
                              fontSize: '14px',
                              borderRadius: '0 0 4px 4px',
                            },
                            '& .ql-toolbar': {
                              borderRadius: '4px 4px 0 0',
                              background: '#f9f9f9',
                              flexWrap: 'wrap',
                            },
                            '& .ql-editor': { minHeight: '150px' },
                            ...(errors?.questions?.[idx]?.question && {
                              '& .ql-toolbar': { borderColor: '#d32f2f' },
                              '& .ql-container': { borderColor: '#d32f2f' },
                            }),
                          }}
                        >
                          <ReactQuill
                            theme="snow"
                            value={field.value || ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            modules={QUILL_MODULES}
                            formats={QUILL_FORMATS}
                            placeholder="Enter the question (formatting, lists, images…)"
                          />
                        </Box>
                      )}
                    />
                    {errors?.questions?.[idx]?.question && (
                      <FormHelperText error>
                        {errors.questions[idx].question.message}
                      </FormHelperText>
                    )}
                  </Grid>
                  {[1, 2, 3, 4].map(optIdx => (
                    <Grid item xs={12} sm={6} key={optIdx}>
                      <Controller
                        name={`questions.${idx}.options.${optIdx}`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label={`Option ${optIdx}`}
                            error={!!errors?.questions?.[idx]?.options?.[optIdx]}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '&:hover fieldset': {
                                  borderColor: '#667eea',
                                },
                              }
                            }}
                          />
                        )}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name={`questions.${idx}.answer`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Correct Answer"
                          error={!!errors?.questions?.[idx]?.answer}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name={`questions.${idx}.duration_in_sec`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Duration (seconds)"
                          type="number"
                          inputProps={{ min: "1" }}
                          error={!!errors?.questions?.[idx]?.duration_in_sec}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name={`questions.${idx}.marks`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Marks"
                          type="number"
                          inputProps={{ min: "1" }}
                          error={!!errors?.questions?.[idx]?.marks}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                </React.Fragment>
              ))}

              {selectedBankQuestions.length === 0 && <Grid item xs={12}>
                    <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Button
                    onClick={addQuestion}
                    startIcon={<AddIcon />}
                        variant="contained"
                    size="large"
                    sx={{
                          background: 'linear-gradient(45deg, #4caf50, #2e7d32)',
                          color: 'white',
                          px: 5,
                      py: 1.5,
                      borderRadius: 3,
                          fontWeight: 600,
                          fontSize: '1rem',
                          boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)',
                      '&:hover': {
                            background: 'linear-gradient(45deg, #2e7d32, #1b5e20)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)'
                      },
                          transition: 'all 0.3s ease'
                    }}
                  >
                    Add Another Question
                  </Button>
                </Box>
              </Grid>}

                  {/* Question Validation Errors - Only show question-related errors here */}
              {selectedBankQuestions.length === 0 && <Grid item xs={12}>
                    {errors?.questions && formatQuestionErrors(errors).length > 0 && (
                      <Card 
                        data-question-errors
                        sx={{ 
                          p: 3, 
                          bgcolor: 'error.light', 
                          borderRadius: 3,
                          border: '2px solid',
                          borderColor: 'error.main',
                          boxShadow: '0 4px 12px rgba(244, 67, 54, 0.2)',
                          mt: 2
                        }}>
                        <Typography variant="h6" color="error" sx={{ fontWeight: 600, mb: 2 }}>
                          Please fix the following question errors:
                        </Typography>
                        <List sx={{ 
                          bgcolor: 'rgba(255, 255, 255, 0.5)', 
                          borderRadius: 2,
                          py: 1
                        }}>
                          {formatQuestionErrors(errors).map((errorMsg, index) => (
                            <ListItem key={index} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                <ErrorIcon color="error" />
                              </ListItemIcon>
                              <ListItemText 
                                primary={errorMsg}
                                primaryTypographyProps={{
                                  sx: {
                                    color: '#d32f2f',
                                    fontWeight: 500,
                                    fontSize: '0.95rem'
                                  }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Card>
                )}
              </Grid>}
                </Grid>
              </CardContent>
            </Card>

            {/* Submit Button Section */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                sx={{
                  ...gradientButtonStyle,
                  minWidth: 200,
                  height: 56,
                  fontSize: '1.1rem',
                  borderRadius: 3,
                  boxShadow: '0 8px 24px rgba(59, 42, 159, 0.4)',
                  '&:hover': {
                    ...gradientButtonStyle['&:hover'],
                    transform: isSubmitting ? 'none' : 'translateY(-3px)',
                    boxShadow: isSubmitting ? '0 8px 24px rgba(59, 42, 159, 0.4)' : '0 12px 32px rgba(59, 42, 159, 0.5)'
                  },
                  '&:disabled': {
                    opacity: 0.6,
                    cursor: 'not-allowed'
                  },
                  transition: 'all 0.3s ease'
                }}
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                      {editingExam ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingExam ? 'Update Exam' : 'Create Exam'
                  )}
                </Button>
            </Box>
          </form>
        </Box>
      )}

      {/* ======= Question Bank Picker Dialog ======= */}
      <Dialog open={bankPickerOpen} onClose={() => setBankPickerOpen(false)} maxWidth="md" fullWidth keepMounted={false} aria-modal="true">
        <DialogTitle sx={{ fontWeight: 700 }}>
          Select Questions from Bank
          <Typography variant="body2" color="text.secondary">
            Choose the questions you want to include in this exam.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {/* Search + type + subject + topics filter */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search questions..."
              value={bankPickerSearch}
              onChange={(e) => { setBankPickerSearch(e.target.value); setBankPickerPage(0); }}
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>All Types</InputLabel>
              <Select
                value={bankPickerType}
                label="All Types"
                onChange={(e) => { setBankPickerType(e.target.value); setBankPickerPage(0); }}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="multiple-choice">Multiple Choice</MenuItem>
                <MenuItem value="multiple-select">Multiple Select</MenuItem>
                <MenuItem value="true-false">True/False</MenuItem>
                <MenuItem value="fill-in-the-blank">Fill in the Blank</MenuItem>
                <MenuItem value="short-answer">Short Answer</MenuItem>
                <MenuItem value="essay">Essay</MenuItem>
                <MenuItem value="matching">Matching</MenuItem>
                <MenuItem value="ordering">Ordering</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Subject</InputLabel>
              <Select
                value={bankPickerSubjectId}
                label="Subject"
                onChange={(e) => { setBankPickerSubjectId(e.target.value); setBankPickerPage(0); }}
              >
                <MenuItem value="">All subjects</MenuItem>
                {(subjects || []).map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              multiple
              size="small"
              sx={{ minWidth: 240 }}
              options={bankPickerTopicCatalog}
              getOptionLabel={(o) => o?.label || o?.name || ""}
              isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
              value={bankPickerTopicCatalog.filter((o) => bankPickerTopicIds.includes(Number(o.id)))}
              onChange={(_, vals) => { setBankPickerTopicIds(vals.map((v) => Number(v.id))); setBankPickerPage(0); }}
              disabled={!bankPickerSubjectId || bankPickerTopicCatalog.length === 0}
              renderInput={(params) => <TextField {...params} label="Topics" placeholder={bankPickerSubjectId ? "All topics" : "Pick a subject first"} />}
            />
          </Box>

          {/* Questions list */}
          {bankPickerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : bankPickerQuestions.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No questions found.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {bankPickerQuestions.map((q) => {
                const alreadyAdded = selectedBankQuestions.some((sq) => sq.id === q.id);
                const inCurrentSelection = bankPickerSelected.some((sq) => sq.id === q.id);
                return (
                  <Paper
                    key={q.id}
                    variant="outlined"
                    onClick={() => toggleBankPickerSelect(q)}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      cursor: 'pointer',
                      opacity: 1,
                      borderColor: inCurrentSelection ? 'primary.main' : '#e0e0e0',
                      bgcolor: inCurrentSelection ? 'primary.50' : 'transparent',
                      transition: 'all 0.15s',
                      '&:hover': { bgcolor: inCurrentSelection ? 'primary.50' : '#f5f5f5' },
                    }}
                  >
                    <Checkbox
                      checked={inCurrentSelection || alreadyAdded}
                      size="small"
                      sx={{ mt: -0.5 }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleBankPickerSelect(q)}
                    />
                    {q.image_url && (
                      <Box sx={{ flexShrink: 0 }}>
                        <img
                          src={q.image_url}
                          alt="Question"
                          style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid #e0e0e0' }}
                        />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Chip label={`ID: ${q.id}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                        <Chip label={q.type?.replace(/-/g, ' ') || 'MCQ'} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                        <Chip label={q.difficulty || 'Medium'} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                        {alreadyAdded && <Chip label="Already added" size="small" color="success" sx={{ fontSize: '0.7rem', height: 20 }} />}
                      </Box>
                      <Box sx={{ maxHeight: 110, overflow: 'auto' }}>
                        <HtmlWithLatex html={q.question_text || ''} />
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {q.default_marks || 1}m
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          )}

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Page {bankPickerPage + 1} of {Math.max(1, Math.ceil(bankPickerTotal / 5))}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" disabled={bankPickerPage === 0} onClick={() => setBankPickerPage((p) => p - 1)} sx={{ textTransform: 'none' }}>
                Previous
              </Button>
              <Button size="small" disabled={(bankPickerPage + 1) * 5 >= bankPickerTotal} onClick={() => setBankPickerPage((p) => p + 1)} sx={{ textTransform: 'none' }}>
                Next
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Typography variant="body2" sx={{ flex: 1, pl: 2, color: 'text.secondary' }}>
            {bankPickerSelected.length} question(s) selected
          </Typography>
          <Button onClick={() => setBankPickerOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={confirmBankSelection}
            disabled={bankPickerSelected.length === 0}
            sx={{ background: 'linear-gradient(to right, #3f2b96, #a71d31)', textTransform: 'none', fontWeight: 600 }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced View Exam Details Modal */}
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="md"
        fullWidth
        keepMounted={false}
        aria-modal="true"
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 3,
          px: 4
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <QuizIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {viewingExam?.name}
          </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Exam Questions & Details
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={() => setViewDialog(false)}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
          {viewingExam && (
            <Box>
              <Card sx={{ 
                mb: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <QuizIcon sx={{ fontSize: 20, flexShrink: 0, opacity: 0.95 }} />
                          <span>
                            {viewingExam.questions_count ??
                              (viewingExam.question_bank_questions?.length || viewingExam.questions?.length || 0)}{' '}
                            Questions
                          </span>
                        </Box>
                      }
                      sx={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '1rem',
                        height: 36,
                        '& .MuiChip-label': { display: 'flex', alignItems: 'center', px: 1.5 },
                      }}
                    />
                    <Chip
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TimeIcon sx={{ fontSize: 20, flexShrink: 0, opacity: 0.95 }} />
                          <span>{viewingExam.duration_minutes} minutes</span>
                        </Box>
                      }
                      sx={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '1rem',
                        height: 36,
                        '& .MuiChip-label': { display: 'flex', alignItems: 'center', px: 1.5 },
                      }}
                    />
                    <Chip
                      label={formatExamPrice(viewingExam.price, viewingExam.currency)}
                      sx={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '1rem',
                        height: 36,
                        '& .MuiChip-label': { px: 1.5, lineHeight: 1.25 },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>

              {viewDialogQuestions.length === 0 && (
                <Typography variant="body1" color="text.secondary" sx={{ py: 2 }}>
                  No questions are attached to this exam.
                </Typography>
              )}

              {viewDialogQuestions.map((question, index) => (
                <Card key={question.key} sx={{ 
                  mb: 3, 
                  borderRadius: 3,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                  background: 'rgba(255,255,255,0.95)',
                  overflow: 'hidden',
                  border: '2px solid transparent',
                  '&:hover': {
                    borderColor: '#667eea',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 32px rgba(102, 126, 234, 0.2)'
                  },
                  transition: 'all 0.3s ease'
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: 2, 
                      mb: 3,
                      pb: 2,
                      borderBottom: '2px solid',
                      borderColor: 'divider'
                    }}>
                      <Box sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '1.2rem',
                        flexShrink: 0,
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                      }}>
                        {index + 1}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <HtmlWithLatex
                          html={question.question_text}
                          style={{ color: '#263238', marginBottom: 4, fontWeight: 600 }}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {question.source === 'bank' && (
                            <Chip
                              label="Question bank"
                              size="small"
                              sx={{
                                background: 'linear-gradient(45deg, #5c6bc0, #3949ab)',
                                color: 'white',
                                fontWeight: 600
                              }}
                            />
                          )}
                          {question.type && (
                            <Chip label={question.type} size="small" variant="outlined" />
                          )}
                          {question.category_name && (
                            <Chip label={question.category_name} size="small" variant="outlined" />
                          )}
                          <Chip 
                            label={`${question.marks} marks`} 
                            size="small"
                            sx={{
                              background: 'linear-gradient(45deg, #4caf50, #2e7d32)',
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                          <Chip 
                            label={
                              question.duration_in_sec != null && question.duration_in_sec !== ''
                                ? `${question.duration_in_sec}s`
                                : '—'
                            } 
                            size="small"
                            sx={{
                              background: 'linear-gradient(45deg, #ff9800, #f57c00)',
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                        </Box>
                      </Box>
                  </Box>

                    {question.options.length > 0 ? (
                    <Grid container spacing={2}>
                    {question.options.map((option, optIndex) => {
                      const isCorrect = question.isCorrectOption(option);
                      return (
                      <Grid item xs={12} sm={6} key={option.id != null ? String(option.id) : `opt-${optIndex}`}>
                        <Box sx={{
                            p: 2,
                            border: '2px solid',
                          borderColor: isCorrect ? '#4caf50' : '#e0e0e0',
                            borderRadius: 2,
                            background: isCorrect
                              ? 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)' 
                              : '#f9f9f9',
                          display: 'flex',
                          alignItems: 'center',
                            gap: 2,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'translateX(4px)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }
                          }}>
                            <Box sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              background: isCorrect
                                ? 'linear-gradient(135deg, #4caf50, #2e7d32)' 
                                : '#e0e0e0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.9rem',
                              flexShrink: 0
                            }}>
                              {String.fromCharCode(65 + optIndex)}
                            </Box>
                          <HtmlWithLatex
                            html={option.option_text}
                            style={{
                              fontWeight: isCorrect ? 600 : 400,
                              color: isCorrect ? '#2e7d32' : 'inherit',
                              flex: 1,
                            }}
                          />
                            {isCorrect && (
                              <Box sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #4caf50, #2e7d32)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '0.8rem',
                                fontWeight: 700
                              }}>
                                ✓
                              </Box>
                            )}
                        </Box>
                      </Grid>
                      );
                    })}
                  </Grid>
                    ) : question.answer?.correct_answer_text ? (
                      <Box sx={{ fontStyle: 'italic' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Correct answer:
                        </Typography>
                        <HtmlWithLatex
                          html={String(question.answer.correct_answer_text ?? '')}
                          style={{ fontSize: '0.875rem', color: 'rgba(0,0,0,0.6)' }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No options for this question.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        keepMounted={false}
        aria-modal="true"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
          color: 'white',
          py: 2.5,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DeleteIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Delete Mock Exam
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this mock exam?
          </Typography>
          {deleteDialog.exam && (
            <Box sx={{ 
              p: 2, 
              bgcolor: 'grey.50', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {deleteDialog.exam.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {deleteDialog.exam.description?.substring(0, 100)}...
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 500 }}>
            This action cannot be undone.
          </Typography>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={deleteDialog.deleteQuestionBankToo}
                onChange={(e) => setDeleteDialog((prev) => ({
                  ...prev,
                  deleteQuestionBankToo: e.target.checked,
                }))}
                disabled={!canDeleteQuestionBank}
              />
            }
            label="Also delete related Question Bank questions"
          />
          {deleteDialog.deleteQuestionBankToo && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Related Question Bank questions that are not linked to other mock exams will be deleted.
            </Alert>
          )}
          {!canDeleteQuestionBank && (
            <Typography variant="caption" color="text.secondary">
              You do not have permission to delete Question Bank questions.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{
              borderColor: 'grey.400',
              color: 'grey.700',
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deletingExamId !== null}
            startIcon={deletingExamId !== null ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
                boxShadow: '0 6px 16px rgba(244, 67, 54, 0.4)'
              }
            }}
          >
            {deletingExamId !== null ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={schoolAssessments.open} onClose={() => setSchoolAssessments({ open: false, school: null, papers: [], mock_exams: [] })} maxWidth="sm" fullWidth>
        <DialogTitle>{schoolAssessments.school?.name || 'School'} — papers and mock exams</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Papers</Typography>
          {(schoolAssessments.papers || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No papers linked.</Typography>
          ) : schoolAssessments.papers.map((p) => (
            <Typography key={p.id} variant="body2" sx={{ py: 0.5 }}>{p.name}</Typography>
          ))}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Mock exams</Typography>
          {(schoolAssessments.mock_exams || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No mock exams linked.</Typography>
          ) : schoolAssessments.mock_exams.map((m) => (
            <Typography key={m.id} variant="body2" sx={{ py: 0.5 }}>{m.name}</Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchoolAssessments({ open: false, school: null, papers: [], mock_exams: [] })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Bank Question Edit Dialog ─────────────────────────────────────── */}
      {editingBankQuestion && (
        <QuestionBankQuestionEditDialog
          question={editingBankQuestion}
          onClose={() => setEditingBankQuestion(null)}
          onSave={saveBankQuestionEdit}
          saving={bankQuestionSaving}
        />
      )}
    </Box>
  );
};

export default MockExam;
